import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { StyledQrCode } from '../../../security/qr/StyledQrCode';
import { WindowControls } from './WindowControls';
import { simulationApi } from '../services/simulationApi';
import { api } from '../../../api/client';
import { MOCK_USERS_BY_ROLE } from './SimulatedUserCardSelector';

/** Logins demo reais (senha Admin@2026) para bypass no EMU/PWA do admin. */
const ADMIN_FIXED_MOBILE_LOGINS: Array<{
  roleKey: 'professor' | 'responsavel' | 'aluno';
  email: string;
  redirect: string;
}> = [
  { roleKey: 'professor', email: 'prof.0.0@edu.mock', redirect: '/professor/dashboard' },
  { roleKey: 'responsavel', email: 'responsavel1@alunoconectado.com.br', redirect: '/responsavel/dashboard' },
  { roleKey: 'aluno', email: 'aluno1@alunoconectado.com.br', redirect: '/aluno/dashboard' },
];

const ADMIN_FIXED_MOBILE_USERS = ADMIN_FIXED_MOBILE_LOGINS.map((entry) => {
  const mock = (MOCK_USERS_BY_ROLE[entry.roleKey] || [])[0];
  return {
    id: mock?.id || `fixed-${entry.roleKey}`,
    name: mock?.name || entry.roleKey,
    role: mock?.role || entry.roleKey,
    detail: mock?.detail || entry.email,
    avatar: mock?.avatar || '?',
    color: mock?.color || '#89b4fa',
    email: entry.email,
    roleKey: entry.roleKey,
    redirect: entry.redirect,
  };
});

export interface PhoneSimulatorProps {
  device: 'CELULAR1' | 'CELULAR2' | 'CELULAR3' | 'CELULAR4' | 'PROFESSOR' | 'RESPONSAVEL' | 'ALUNO';
  actorId?: string;
  /** Quando aberto pelo footer da escola — menu de perfil lista usuários desta unidade. */
  schoolId?: string;
  adbPort?: string;
  wsPort?: string;
  emulatorPort?: number;
  userRole?: string;
  initialUrl?: string;
  onClose?: () => void;
  onMinimize?: () => void;
  onMaximize?: () => void;
  isMaximized?: boolean;
  onSnapLayout?: (
    snapType:
      | 'left' | 'right' | 'top' | 'grid-tl' | 'grid-tr' | 'grid-bl' | 'grid-br'
      | 'col4-1' | 'col4-2' | 'col4-3' | 'col4-4'
      | 'col25-75-l' | 'col25-75-r'
      | 'col75-25-l' | 'col75-25-r'
      | 'col50-25-25-l' | 'col50-25-25-m' | 'col50-25-25-r'
      | 'col25-25-50-l' | 'col25-25-50-m' | 'col25-25-50-r'
  ) => void;
  onDragStart?: (e: React.MouseEvent) => void;
  onRotate?: (isLandscape: boolean) => void;
  isBackendOnline?: boolean;
  /** Painel embutido na janela da escola: sem carcaça/status/nav nativa. */
  compactEmbed?: boolean;
  /** Menu de ferramentas (sanduíche no header da coluna) — só com compactEmbed. */
  toolsMenuOpen?: boolean;
  onToolsMenuOpenChange?: (open: boolean) => void;
  /** Professor da mesma janela — para parear responsável da turma (push na chamada). */
  pairedProfessorId?: string;
}

const SPEED_MAP: Record<string, number> = {
  SUPER_LENTA: 5,
  LENTA: 15,
  REALISTA: 40,
  ACELERADA: 80,
  RAPIDA: 120,
  MUITO_RAPIDA: 200,
};

const OSRM_PROFILES: Record<string, string> = {
  CARRO: 'driving',
  MOTO: 'driving',
  BICICLETA: 'cycling',
  A_PE: 'foot',
};

const PHONE_PWA_REDIRECT: Record<string, string> = {
  PROFESSOR: '/professor/dashboard',
  RESPONSAVEL: '/responsavel/dashboard',
  ALUNO: '/aluno/dashboard',
};

/**
 * Login isolado por iframe (sessionStorage via ?impersonate=).
 * Evita autoLoginToken → localStorage, que faz PROF e RESP virarem o mesmo usuário.
 */
function buildPhoneImpersonateSrc(
  token: string,
  user: Record<string, unknown> | null | undefined,
  device: string,
  opts?: { promptNotification?: boolean },
): string {
  const perfil = String(user?.perfil || device).toUpperCase();
  const wantPush = opts?.promptNotification !== false;
  const payload = {
    access_token: token,
    user: user || { perfil: device },
    redirect_url: PHONE_PWA_REDIRECT[perfil] || PHONE_PWA_REDIRECT[device] || '/',
    skip_splash: true,
    from_cockpit: true,
    // Persistido no AuthProvider — URL some no redirect
    promptNotification:
      wantPush || perfil === 'RESPONSAVEL' || perfil === 'PROFESSOR',
  };
  let url = `${window.location.origin}/login?impersonate=${encodeURIComponent(JSON.stringify(payload))}`;
  if (wantPush || perfil === 'RESPONSAVEL' || perfil === 'PROFESSOR') {
    url += '&promptNotification=1';
  }
  return url;
}

export const PhoneSimulatorContainer: React.FC<PhoneSimulatorProps> = ({
  device,
  actorId,
  schoolId,
  adbPort: propAdbPort,
  wsPort: propWsPort,
  emulatorPort: propEmulatorPort,
  initialUrl,
  onClose,
  onMinimize,
  onMaximize,
  isMaximized = false,
  onSnapLayout,
  onDragStart,
  onRotate,
  isBackendOnline = true,
  compactEmbed = false,
  toolsMenuOpen = false,
  onToolsMenuOpenChange,
  pairedProfessorId,
}) => {
  // Resolve as portas padrão
  const adbPort = propAdbPort || (
    device === 'PROFESSOR' || device === 'CELULAR1' ? '5555' : 
    device === 'RESPONSAVEL' || device === 'CELULAR2' ? '5556' : 
    device === 'ALUNO' || device === 'CELULAR3' ? '5557' : '5558'
  );
  const wsPort = propWsPort || (
    device === 'PROFESSOR' || device === 'CELULAR1' ? '8886' : 
    device === 'RESPONSAVEL' || device === 'CELULAR2' ? '8887' : 
    device === 'ALUNO' || device === 'CELULAR3' ? '8888' : '8889'
  );
  const emulatorPort = propEmulatorPort || (
    device === 'PROFESSOR' || device === 'CELULAR1' ? 8554 : 
    device === 'RESPONSAVEL' || device === 'CELULAR2' ? 8555 : 
    device === 'ALUNO' || device === 'CELULAR3' ? 8556 : 8557
  );

  const isAdmin = (() => {
    try {
      const userObj = JSON.parse(localStorage.getItem('ac_user') || 'null');
      return userObj && userObj.perfil === 'ADMIN';
    } catch {
      return false;
    }
  })();

  const [rightViewMode, setRightViewMode] = useState<'EMULATOR' | 'WEB_PWA'>('WEB_PWA');

  // EMU (ADB/scrcpy) só para ADMIN — demais perfis / embed compacto ficam em PWA
  useEffect(() => {
    if ((compactEmbed || !isAdmin) && rightViewMode !== 'WEB_PWA') {
      setRightViewMode('WEB_PWA');
    }
  }, [isAdmin, compactEmbed, rightViewMode]);

  const closeToolsMenu = () => onToolsMenuOpenChange?.(false);
  const runToolAction = (fn: () => void) => {
    fn();
    closeToolsMenu();
  };
  const [isLandscape, setIsLandscape] = useState<boolean>(false);
  const [navigationMode, setNavigationMode] = useState<'3BUTTON' | 'GESTURE'>('3BUTTON');
  const [isWifiOn, setIsWifiOn] = useState<boolean>(true);
  const [isDataOn, setIsDataOn] = useState<boolean>(true);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(true);
  const [isEmulatorSettingsOpen, setIsEmulatorSettingsOpen] = useState<boolean>(false);
  const [phoneTime, setPhoneTime] = useState<string>('12:00');
  const [iframeReloadKey, setIframeReloadKey] = useState<number>(0);
  const [isStartingDevice, setIsStartingDevice] = useState<boolean>(false);
  const [isDeviceOnline, setIsDeviceOnline] = useState<boolean>(true);
  const [bootStatusMessage, setBootStatusMessage] = useState<string>('');

  useEffect(() => {
    if (!isStartingDevice) {
      setBootStatusMessage('');
      return;
    }
    setBootStatusMessage('Contatando Google Cloud...');
    const interval = setInterval(async () => {
      try {
        const res = await fetch('/api/v1/dev/adb/boot-status');
        const data = await res.json();
        if (data.status) {
          setBootStatusMessage(data.status);
        }
      } catch {}
    }, 1500);
    return () => clearInterval(interval);
  }, [isStartingDevice]);

  // Estados de Falha de Conexão e Diálogos
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const [isFailNotificationDismissed, setIsFailNotificationDismissed] = useState<boolean>(false);
  const [hasPromptedRevert, setHasPromptedRevert] = useState<boolean>(false);
  const [showRevertPrompt, setShowRevertPrompt] = useState<boolean>(false);
  const [isContextMenuOpen, setIsContextMenuOpen] = useState<boolean>(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState<boolean>(false);
  const [schoolActors, setSchoolActors] = useState<Array<{
    id: string;
    nome: string;
    email?: string | null;
    perfil?: string | null;
    cargo?: string | null;
    fotoUrl?: string | null;
  }>>([]);
  const [schoolActorsLoading, setSchoolActorsLoading] = useState(false);
  const [connectingActorId, setConnectingActorId] = useState<string | null>(null);
  const [localActorId, setLocalActorId] = useState<string | undefined>(actorId);
  const [pwaAuthToken, setPwaAuthToken] = useState<string | null>(null);
  const [pwaAuthUser, setPwaAuthUser] = useState<Record<string, unknown> | null>(null);

  const applyPwaSession = (token: string, user?: Record<string, unknown> | null) => {
    setPwaAuthToken(token);
    setPwaAuthUser(user || { perfil: device });
  };

  useEffect(() => {
    setLocalActorId(actorId);
  }, [actorId]);

  const effectiveActorId = localActorId || actorId;

  // Escola vinculada: carrega usuários para o menu de seleção de perfil
  useEffect(() => {
    if (!schoolId) {
      setSchoolActors([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setSchoolActorsLoading(true);
      try {
        const actors = await simulationApi.getSchoolActors(schoolId);
        if (!cancelled) {
          // Mobile: prioriza perfis com app (Prof / Resp / Aluno); inclui equipe se tiver e-mail
          const filtered = (actors || []).filter((a: any) => {
            const p = (a.perfil || '').toUpperCase();
            if (p === 'PROFESSOR' || p === 'RESPONSAVEL' || p === 'ALUNO') return true;
            if (p === 'ESCOLA' && a.email) return true;
            return false;
          });
          setSchoolActors(filtered);
        }
      } catch (err) {
        console.error('[PhoneSimulator] falha ao carregar usuários da escola', err);
        if (!cancelled) setSchoolActors([]);
      } finally {
        if (!cancelled) setSchoolActorsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [schoolId]);

  // Menu de perfil: só se NÃO for auto-login (PROF/RESP/ALUNO embutidos na janela escola)
  useEffect(() => {
    const autoRole = ['PROFESSOR', 'RESPONSAVEL', 'ALUNO'].includes(String(device).toUpperCase());
    if (schoolId && autoRole) return; // auto-login cuida disso
    if ((schoolId || isAdmin) && !effectiveActorId) {
      setIsContextMenuOpen(true);
    }
  }, [schoolId, isAdmin, effectiveActorId, device]);

  // Estados e Refs de Gravação de Tela / Screenshot / Logs de Erro
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [isErrorLogsModalOpen, setIsErrorLogsModalOpen] = useState<boolean>(false);
  const [errorLogs, setErrorLogs] = useState<Array<{ message: string; stack?: string; time: string; source: string }>>([]);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const recordingMimeRef = useRef<{ mimeType: string; ext: string }>({ mimeType: 'video/webm', ext: 'webm' });

  // Lógica de drag overlay
  const [showDragOverlay, setShowDragOverlay] = useState<boolean>(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasAutoStartedRef = useRef<boolean>(false);

  // Lógica de digitação/teclado
  interface FocusedInputMeta {
    id: string;
    name: string;
    label: string;
    value: string;
    element: HTMLInputElement | HTMLTextAreaElement;
  }
  const [activeInput, setActiveInput] = useState<FocusedInputMeta | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Estados do Relógio e Clima para São Paulo ---
  const [spTime, setSpTime] = useState<string>('');
  const [spDate, setSpDate] = useState<string>('');

  useEffect(() => {
    const updateTime = () => {
      try {
        const optionsTime: Intl.DateTimeFormatOptions = {
          timeZone: 'America/Sao_Paulo',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        };
        const optionsDate: Intl.DateTimeFormatOptions = {
          timeZone: 'America/Sao_Paulo',
          day: '2-digit',
          month: 'short'
        };
        const now = new Date();
        setSpTime(now.toLocaleTimeString('pt-BR', optionsTime));
        setSpDate(now.toLocaleDateString('pt-BR', optionsDate));
      } catch (err) {
        const now = new Date();
        setSpTime(now.toLocaleTimeString('pt-BR'));
        setSpDate(now.toLocaleDateString('pt-BR'));
      }
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const getSpWeather = () => {
    const hour = parseInt(spTime.split(':')[0]) || new Date().getHours();
    if (hour >= 6 && hour < 12) {
      return { icon: '🌤️', temp: '21°C', desc: 'Nublado' };
    } else if (hour >= 12 && hour < 18) {
      return { icon: '☀️', temp: '25°C', desc: 'Ensolarado' };
    } else if (hour >= 18 && hour < 22) {
      return { icon: '🌌', temp: '19°C', desc: 'Limpo' };
    } else {
      return { icon: '🌙', temp: '16°C', desc: 'Limpo' };
    }
  };

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // --- Estados de Geolocalização / Roteamento ---
  const [isLocationModalOpen, setIsLocationModalOpen] = useState<boolean>(false);
  const [addressQuery, setAddressQuery] = useState<string>('');
  const [locationResult, setLocationResult] = useState<{ display_name: string; lat: number; lon: number } | null>(null);
  const [isSearchingLocation, setIsSearchingLocation] = useState<boolean>(false);
  const [isGettingLocation, setIsGettingLocation] = useState<boolean>(false);
  const [savedAddresses, setSavedAddresses] = useState<Array<{ display_name: string; lat: number; lon: number }>>(() => {
    try {
      return JSON.parse(localStorage.getItem(`emulator_saved_addresses_${device}`) || '[]');
    } catch {
      return [];
    }
  });

  const [isRouteMode, setIsRouteMode] = useState<boolean>(false);
  const [routeSpeed, setRouteSpeed] = useState<string>('REALISTA');
  const [routeVehicle, setRouteVehicle] = useState<string>('CARRO');
  const [waypoints, setWaypoints] = useState<Array<{ display_name: string; lat: number; lon: number }>>([]);
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  const [isRouteRunning, setIsRouteRunning] = useState<boolean>(false);
  const [routeProgress, setRouteProgress] = useState<string>('');

  const routeIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const routePointsRef = useRef<Array<[number, number]>>([]);
  const routeIndexRef = useRef<number>(0);
  const [showSlowBootWarning, setShowSlowBootWarning] = useState<boolean>(false);
  const bootTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const routeCacheRef = useRef<Record<string, Array<[number, number]>>>(
    (() => { try { return JSON.parse(localStorage.getItem('emulator_route_cache') || '{}'); } catch { return {}; } })()
  );

  // Time clock update
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setPhoneTime(
        now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      );
    };
    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, []);

  // Monitora online/offline do emulador e sincroniza os estados reais do dispositivo
  const refreshDeviceState = async () => {
    if (rightViewMode !== 'EMULATOR' || !isBackendOnline) return;
    try {
      // Consulta se o dispositivo está online
      const onlineRes = await fetch('/api/v1/dev/adb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device, command: 'check_online' })
      });
      const onlineData = await onlineRes.json();
      const online = onlineData.success && onlineData.output === '1';
      setIsDeviceOnline(online);

      if (online) {
        // Query Wifi
        const wifiRes = await fetch('/api/v1/dev/adb', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ device, command: 'wifi_status' })
        });
        const wifiData = await wifiRes.json();
        if (wifiData.success) {
          setIsWifiOn(wifiData.output === '1');
        }

        // Query Mobile Data
        const dataRes = await fetch('/api/v1/dev/adb', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ device, command: 'data_status' })
        });
        const dataData = await dataRes.json();
        if (dataData.success) {
          setIsDataOn(dataData.output === '1');
        }

        // Query Dark Mode
        const darkRes = await fetch('/api/v1/dev/adb', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ device, command: 'dark_status' })
        });
        const darkData = await darkRes.json();
        if (darkData.success) {
          setIsDarkMode(darkData.output.includes('yes'));
        }

        // Query Rotation
        const rotRes = await fetch('/api/v1/dev/adb', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ device, command: 'rotation_status' })
        });
        const rotData = await rotRes.json();
        if (rotData.success) {
          setIsLandscape(rotData.output === '1' || rotData.output === '3');
        }
      }
    } catch (err) {
      console.warn("Erro ao ler estado do emulador:", err);
      setIsDeviceOnline(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    refreshDeviceState();
    const timer = setInterval(refreshDeviceState, 10000);
    return () => clearInterval(timer);
  }, [device, rightViewMode, isBackendOnline, isAdmin]);
  // Efeito de contagem regressiva para falha de conexão do emulador (15s)
  useEffect(() => {
    if (!isAdmin) return;
    if (rightViewMode !== 'EMULATOR' || isDeviceOnline || !isBackendOnline) {
      setElapsedSeconds(0);
      return;
    }

    const timer = setInterval(() => {
      setElapsedSeconds(prev => {
        const next = prev + 1;
        if (next >= 15) {
          clearInterval(timer);
          // Marca a falha na sessão para hoje
          sessionStorage.setItem(`ac_emulator_failed_${device}`, 'true');
          setRightViewMode('WEB_PWA');
          setElapsedSeconds(0);
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [rightViewMode, isDeviceOnline, isBackendOnline, device, isAdmin]);

  // Monitora se o emulador se recuperou e pergunta se deseja voltar a usá-lo
  useEffect(() => {
    if (!isAdmin) return;
    if (rightViewMode === 'EMULATOR') return;

    const isFailedToday = sessionStorage.getItem(`ac_emulator_failed_${device}`) === 'true';
    if (!isFailedToday || hasPromptedRevert) return;

    // Se o dispositivo ficou online
    if (isDeviceOnline && isBackendOnline) {
      setHasPromptedRevert(true);
      setShowRevertPrompt(true);
    }
  }, [isDeviceOnline, isBackendOnline, rightViewMode, hasPromptedRevert, device, isAdmin]);

  // Auto-bypass do Chrome ao detectar o dispositivo online no modo emulador
  useEffect(() => {
    if (isDeviceOnline && isBackendOnline && rightViewMode === 'EMULATOR') {
      fetch('/api/v1/dev/adb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device, command: 'bypass_chrome' })
      }).catch(() => {});
    }
  }, [isDeviceOnline, isBackendOnline, rightViewMode, device]);

  // Liga o emulador automaticamente ao alternar ou inicializar no modo EMULATOR offline
  useEffect(() => {
    if (rightViewMode === 'EMULATOR' && !isDeviceOnline && !isStartingDevice && !hasAutoStartedRef.current && isBackendOnline) {
      hasAutoStartedRef.current = true;
      startDevice();
    }
  }, [rightViewMode, isDeviceOnline, isStartingDevice, isBackendOnline, device]);
  // Limpa o aviso de boot lento se o dispositivo ficar online
  useEffect(() => {
    if (isDeviceOnline) {
      if (bootTimerRef.current) {
        clearTimeout(bootTimerRef.current);
        bootTimerRef.current = null;
      }
      setShowSlowBootWarning(false);
    }
  }, [isDeviceOnline]);

  // Limpa timers ao desmontar
  useEffect(() => {
    return () => {
      if (routeIntervalRef.current) clearInterval(routeIntervalRef.current);
      if (bootTimerRef.current) clearTimeout(bootTimerRef.current);
    };
  }, []);

  // Resolve URL do Emulador para carregar o player Broadway
  const getEmulatorUrl = (): string => {
    const hostname = window.location.hostname;
    const isLocalDev =
      import.meta.env.DEV ||
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.startsWith('192.168.') ||
      hostname.includes('ngrok-free.dev') ||
      hostname.includes('loca.lt');

    if (isLocalDev) {
      const wsProto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const speedParams = 'bitrate=2000000&maxSize=720&fps=60';
      const remotePort = wsPort;
      const wsUrl = `${wsProto}://${window.location.host}/ws-scrcpy/?action=proxy-adb&remote=tcp%3A${remotePort}&udid=127.0.0.1%3A${adbPort}&${speedParams}`;
      return `/ws-scrcpy/#!action=stream&udid=127.0.0.1%3A${adbPort}&player=broadway&${speedParams}&ws=${encodeURIComponent(wsUrl)}`;
    } else {
      const proto = window.location.protocol;
      return `${proto}//${hostname}/emulator/${device.toLowerCase()}/`;
    }
  };

  // Envia eventos para o emulador via ADB
  const sendAndroidKey = async (key: string, codeStr: string, code: number) => {
    try {
      await fetch('/api/v1/dev/adb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device,
          command: 'input_keyevent',
          keyevent: code
        })
      });
      if (iframeRef.current) iframeRef.current.focus();
    } catch (err) {
      console.error('Erro ao injetar tecla no emulador:', err);
    }
  };

  const sendAndroidText = async (text: string) => {
    if (!text) return;
    try {
      await fetch('/api/v1/dev/adb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device,
          command: 'input_text',
          text
        })
      });
    } catch (err) {
      console.error('Erro ao enviar text para o emulador:', err);
    }
  };

  // Liga o emulador (VM GCE + Docker + Adb Local)
  const startDevice = async () => {
    setIsStartingDevice(true);
    setShowSlowBootWarning(false);
    
    if (bootTimerRef.current) {
      clearTimeout(bootTimerRef.current);
    }
    bootTimerRef.current = setTimeout(() => {
      if (!isDeviceOnline) {
        setShowSlowBootWarning(true);
      }
    }, 10000);

    try {
      const res = await fetch('/api/v1/dev/adb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device, command: 'start_device' })
      });
      const data = await res.json();
      if (data.success) {
        setTimeout(() => {
          refreshDeviceState();
          setIsStartingDevice(false);
          setIframeReloadKey(prev => prev + 1);
        }, 8000);
      } else {
        setIsStartingDevice(false);
        if (bootTimerRef.current) {
          clearTimeout(bootTimerRef.current);
          bootTimerRef.current = null;
        }
        toast.error(`Falha ao iniciar emulador: ${data.error || 'Erro interno'}`);
      }
    } catch (err: any) {
      setIsStartingDevice(false);
      if (bootTimerRef.current) {
        clearTimeout(bootTimerRef.current);
        bootTimerRef.current = null;
      }
      toast.error(`Erro: ${err.message || 'Sem conexão com servidor'}`);
    }
  };

  // Toggle de recursos nativos via comandos sequenciais válidos do backend
  const toggleNetwork = async () => {
    try {
      const turningOff = isWifiOn || isDataOn;
      setIsWifiOn(!turningOff);
      setIsDataOn(!turningOff);

      await fetch('/api/v1/dev/adb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device, command: turningOff ? 'wifi_off' : 'wifi_on' })
      });
      await fetch('/api/v1/dev/adb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device, command: turningOff ? 'data_off' : 'data_on' })
      });
    } catch (err) {
      console.error('Erro ao alternar rede:', err);
    }
  };

  const toggleDarkMode = async () => {
    try {
      const command = isDarkMode ? 'dark_off' : 'dark_on';
      setIsDarkMode(!isDarkMode);
      await fetch('/api/v1/dev/adb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device, command })
      });
    } catch (err) {
      console.error('Erro ao alternar modo escuro:', err);
    }
  };

  const toggleRotation = async () => {
    try {
      const nextLandscape = !isLandscape;
      const command = nextLandscape ? 'landscape' : 'portrait';
      setIsLandscape(nextLandscape);
      onRotate?.(nextLandscape);
      await fetch('/api/v1/dev/adb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device, command })
      });
    } catch (err) {
      console.error('Erro ao rotacionar tela:', err);
    }
  };

  // --- Lógica de Captura / Gravação de Vídeo / Screenshots ---
  const getIframeMediaElement = (): HTMLVideoElement | HTMLCanvasElement | null => {
    if (!iframeRef.current) return null;
    try {
      const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
      if (!doc) return null;
      const video = doc.querySelector('video') as HTMLVideoElement | null;
      if (video && video.readyState >= 2) return video;
      const canvas = (doc.querySelector('canvas.video-layer') || doc.querySelector('canvas')) as HTMLCanvasElement | null;
      if (canvas && canvas.width > 0) return canvas;
      return null;
    } catch {
      return null;
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const startRecording = async () => {
    try {
      let stream: MediaStream;
      const mediaEl = getIframeMediaElement();

      if (mediaEl) {
        if (mediaEl instanceof HTMLVideoElement) {
          stream = (mediaEl as any).captureStream(30);
        } else {
          stream = (mediaEl as any).captureStream(30);
        }
      } else {
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: { displaySurface: 'browser' } as any,
          audio: false,
        });
      }

      mediaStreamRef.current = stream;
      recordedChunksRef.current = [];

      let mimeType = 'video/webm';
      let ext = 'webm';

      if (MediaRecorder.isTypeSupported('video/mp4;codecs=avc1')) {
        mimeType = 'video/mp4;codecs=avc1';
        ext = 'mp4';
      } else if (MediaRecorder.isTypeSupported('video/mp4')) {
        mimeType = 'video/mp4';
        ext = 'mp4';
      } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) {
        mimeType = 'video/webm;codecs=vp9';
        ext = 'webm';
      } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) {
        mimeType = 'video/webm;codecs=vp8';
        ext = 'webm';
      } else if (MediaRecorder.isTypeSupported('video/webm')) {
        mimeType = 'video/webm';
        ext = 'webm';
      }

      recordingMimeRef.current = { mimeType, ext };
      const recorder = new MediaRecorder(stream, { mimeType });

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const { mimeType: finalMime, ext: finalExt } = recordingMimeRef.current;
        const blob = new Blob(recordedChunksRef.current, { type: finalMime });
        downloadBlob(blob, `gravacao_${new Date().toISOString().replace(/[:.]/g, '-')}.${finalExt}`);
        setIsRecording(false);
      };

      stream.getVideoTracks()[0].onended = () => {
        if (mediaRecorderRef.current?.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
        setIsRecording(false);
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Erro ao iniciar gravação:', err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
  };

  const openAuthenticatedApp = async (customToken?: string | React.MouseEvent) => {
    const adminToken = localStorage.getItem('ac_token');
    let token = typeof customToken === 'string' ? customToken : '';
    
    const sessionsStr = localStorage.getItem('ac_sessions');
    if (sessionsStr) {
      try {
        const sessions = JSON.parse(sessionsStr);
        if (effectiveActorId && sessions[effectiveActorId]) {
          token = sessions[effectiveActorId].access_token;
        } else if (device === 'PROFESSOR' && sessions['PROFESSOR']) {
          token = sessions['PROFESSOR'].access_token;
        } else if (device === 'RESPONSAVEL' && sessions['RESPONSAVEL']) {
          token = sessions['RESPONSAVEL'].access_token;
        } else if (device === 'ALUNO' && sessions['ALUNO']) {
          token = sessions['ALUNO'].access_token;
        }
      } catch {}
    }
    if (!token && adminToken) {
      token = adminToken;
    }

    if (!token) {
      toast.error('Nenhum usuário simulado autenticado. Faça login primeiro.');
      return;
    }

    const origin = window.location.origin;
    const targetUrl = `${origin}/login?autoLoginToken=${token}&promptNotification=1`;

    try {
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        const localPort = window.location.port || '3000';
        await fetch('/api/v1/dev/adb', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            device,
            command: 'set_reverse',
            port: localPort
          })
        });
      }

      await fetch('/api/v1/dev/adb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device,
          command: 'open_url',
          url: targetUrl
        })
      });
    } catch (err: any) {
      console.error('Erro ao abrir app:', err);
    }
  };

  const connectSchoolActor = async (actor: {
    id: string;
    nome: string;
    email?: string | null;
    perfil?: string | null;
    cargo?: string | null;
    fotoUrl?: string | null;
  }, opts?: { silent?: boolean }): Promise<boolean> => {
    if (!schoolId) return false;
    setConnectingActorId(actor.id);
    try {
      // Reusa JWT já gerado na abertura da escola (evita round-trip)
      try {
        const existing = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
        const perfilU = (actor.perfil || '').toUpperCase();
        let roleKey = actor.id;
        if (perfilU === 'PROFESSOR') roleKey = 'PROFESSOR';
        else if (perfilU === 'RESPONSAVEL') roleKey = 'RESPONSAVEL';
        else if (perfilU === 'ALUNO') roleKey = 'ALUNO';
        const cached =
          existing[actor.id] ||
          existing[`${schoolId}::${roleKey}`] ||
          existing[roleKey];
        if (
          cached?.access_token &&
          (!cached.schoolId || cached.schoolId === schoolId) &&
          (cached.user?.id === actor.id || !cached.user?.id)
        ) {
          setLocalActorId(actor.id);
          applyPwaSession(cached.access_token, cached.user || {
            id: actor.id,
            nome: actor.nome,
            email: actor.email,
            perfil: actor.perfil,
            cargo: actor.cargo,
            fotoUrl: actor.fotoUrl,
          });
          setIsContextMenuOpen(false);
          setIframeReloadKey((prev) => prev + 1);
          if (rightViewMode === 'EMULATOR') {
            await openAuthenticatedApp(cached.access_token);
          }
          return true;
        }
      } catch { /* segue para /simulation/start */ }

      const res = await api.post('/simulation/start', {
        escolaId: schoolId,
        actorIds: [actor.id],
      });
      const sessionBag = res.data?.sessions || res.data || {};
      const perfil = (actor.perfil || '').toUpperCase();
      const cargo = (actor.cargo || '').toUpperCase();
      let key = actor.id;
      if (perfil === 'PROFESSOR') key = 'PROFESSOR';
      else if (perfil === 'RESPONSAVEL') key = 'RESPONSAVEL';
      else if (perfil === 'ALUNO') key = 'ALUNO';
      else if (perfil === 'ESCOLA' && cargo === 'DIRETOR') key = 'ESCOLA_DIRETOR';
      else if (perfil === 'ESCOLA' && cargo === 'SECRETARIA') key = 'ESCOLA_SECRETARIA';
      else if (perfil === 'ESCOLA' && (cargo === 'COORDENACAO' || cargo === 'COORDENADOR')) {
        key = 'ESCOLA_COORDENACAO';
      }

      const sessionData =
        sessionBag[key] ||
        sessionBag[actor.id] ||
        Object.values(sessionBag).find((s: any) => s?.user?.id === actor.id);

      if (!sessionData || !(sessionData as any).access_token) {
        if (!opts?.silent) toast.error('Não foi possível gerar sessão para este usuário.');
        return false;
      }

      const tagged = {
        ...(sessionData as any),
        schoolId,
        user: (sessionData as any).user || {
          id: actor.id,
          nome: actor.nome,
          email: actor.email,
          perfil: actor.perfil,
          cargo: actor.cargo,
          fotoUrl: actor.fotoUrl,
        },
      };

      const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
      sessions[actor.id] = tagged;
      sessions[key] = tagged;
      sessions[`${schoolId}::${key}`] = tagged;
      localStorage.setItem('ac_sessions', JSON.stringify(sessions));

      setLocalActorId(actor.id);
      applyPwaSession(tagged.access_token, tagged.user);
      setIsContextMenuOpen(false);
      setIframeReloadKey((prev) => prev + 1);

      if (rightViewMode === 'EMULATOR') {
        await openAuthenticatedApp(tagged.access_token);
      }
      return true;
    } catch (err: any) {
      console.error('[PhoneSimulator] connectSchoolActor', err);
      if (!opts?.silent) toast.error(err?.message || 'Falha ao conectar usuário da escola.');
      return false;
    } finally {
      setConnectingActorId(null);
    }
  };

  // Auto-login: ator fixo / primeiro do perfil; RESPONSAVEL = aluno da turma do professor
  const autoLoginKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!schoolId) return;
    const role = String(device).toUpperCase();
    if (role !== 'PROFESSOR' && role !== 'RESPONSAVEL' && role !== 'ALUNO') return;
    if (pwaAuthToken && effectiveActorId) return;
    if (schoolActorsLoading && role !== 'RESPONSAVEL') return;

    const runKey = `${schoolId}:${role}:${effectiveActorId || 'auto'}:${pairedProfessorId || ''}`;
    if (autoLoginKeyRef.current === runKey) return;

    let cancelled = false;

    const applyCached = () => {
      try {
        const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
        const cached =
          (effectiveActorId && sessions[effectiveActorId]) ||
          sessions[`${schoolId}::${role}`] ||
          sessions[role];
        if (
          cached?.access_token &&
          (!cached.schoolId || cached.schoolId === schoolId)
        ) {
          const uid = cached.user?.id || effectiveActorId;
          const p = String(cached.user?.perfil || '').toUpperCase();
          if (!p || p === role || (effectiveActorId && uid === effectiveActorId)) {
            autoLoginKeyRef.current = runKey;
            setLocalActorId(uid);
            applyPwaSession(cached.access_token, cached.user || { id: uid, perfil: role });
            setIsContextMenuOpen(false);
            setIframeReloadKey((prev) => prev + 1);
            return true;
          }
        }
      } catch { /* noop */ }
      return false;
    };

    if (applyCached()) return;

    const run = async () => {
      // RESPONSAVEL: preferir vínculo com turma do professor (push na chamada)
      if (role === 'RESPONSAVEL') {
        let professorId = pairedProfessorId;
        if (!professorId) {
          try {
            const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
            professorId =
              sessions[`${schoolId}::PROFESSOR`]?.user?.id ||
              sessions.PROFESSOR?.user?.id;
          } catch { /* noop */ }
        }
        try {
          const paired = await simulationApi.getPairedResponsavel(schoolId, professorId);
          if (cancelled) return;
          if (paired?.found && paired.responsavel?.id && paired.responsavel.email) {
            const ok = await connectSchoolActor(
              {
                id: paired.responsavel.id,
                nome: paired.responsavel.nome,
                email: paired.responsavel.email,
                perfil: 'RESPONSAVEL',
                fotoUrl: paired.responsavel.fotoUrl,
              },
              { silent: true },
            );
            if (ok) autoLoginKeyRef.current = runKey;
            if (ok || cancelled) return;
          }
        } catch (err) {
          console.warn('[PhoneSimulator] paired-responsavel falhou', err);
        }
      }

      if (schoolActorsLoading) return;

      const byRole = schoolActors.filter((a) => {
        if ((a.perfil || '').toUpperCase() !== role) return false;
        // Responsável sem e-mail = id da tabela responsaveis (não autentica)
        if (role === 'RESPONSAVEL' && !a.email) return false;
        return true;
      });
      const byId = effectiveActorId
        ? schoolActors.find((a) => a.id === effectiveActorId && (role !== 'RESPONSAVEL' || !!a.email))
        : undefined;
      const fixed = byRole.find((a) => String(a.id).startsWith('00000001-'));
      const pick = byId || fixed || byRole[0];
      if (!pick) {
        setIsContextMenuOpen(true);
        return;
      }

      const ok = await connectSchoolActor(pick, { silent: true });
      if (ok) autoLoginKeyRef.current = runKey;
      else setIsContextMenuOpen(true);
    };

    void run();
    return () => { cancelled = true; };
  }, [
    schoolId,
    device,
    effectiveActorId,
    schoolActors,
    schoolActorsLoading,
    pwaAuthToken,
    pairedProfessorId,
  ]);

  /** Admin sem sessão simulada: login bypass nos usuários fixos + prompt de notificação. */
  const connectFixedBypassUser = async (user: (typeof ADMIN_FIXED_MOBILE_USERS)[number]) => {
    setConnectingActorId(user.id);
    try {
      if ('Notification' in window && Notification.permission === 'default') {
        try {
          const permission = await Notification.requestPermission();
          if (permission === 'granted') {
            new Notification('Notificações Ativas', {
              body: `Notificações liberadas para ${user.role}.`,
              icon: '/favicon.ico',
            });
          }
        } catch { /* noop */ }
      }

      const { data } = await api.post('/auth/login', {
        email: user.email,
        senha: 'Admin@2026',
      });
      const token = data?.access_token || data?.accessToken || data?.token;
      const loggedUser = data?.user;
      if (!token || !loggedUser) {
        toast.error('Bypass falhou: login sem token. Verifique se o usuário demo existe (senha Admin@2026).');
        return;
      }

      const sessionKey = user.roleKey.toUpperCase(); // PROFESSOR | RESPONSAVEL | ALUNO
      const tagged = {
        access_token: token,
        user: loggedUser,
        bypassFixed: true,
      };
      const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
      sessions[loggedUser.id] = tagged;
      sessions[sessionKey] = tagged;
      localStorage.setItem('ac_sessions', JSON.stringify(sessions));
      localStorage.setItem(
        `ac_selected_actor_${user.roleKey}`,
        JSON.stringify({ userId: loggedUser.id, userName: loggedUser.nome || user.name }),
      );

      setLocalActorId(loggedUser.id);
      applyPwaSession(token, loggedUser);
      setIsContextMenuOpen(false);
      setIframeReloadKey((prev) => prev + 1);

      if (rightViewMode === 'EMULATOR') {
        await openAuthenticatedApp(token);
      } else if (iframeRef.current) {
        iframeRef.current.src = buildPhoneImpersonateSrc(token, loggedUser, device, { promptNotification: true });
      }
    } catch (err: any) {
      console.error('[PhoneSimulator] connectFixedBypassUser', err);
      toast.error(err?.response?.data?.message || err?.message || 'Falha no bypass do usuário fixo.');
    } finally {
      setConnectingActorId(null);
    }
  };

  const [profileUpdateKey, setProfileUpdateKey] = useState<number>(0);
  const [isUploadingFoto, setIsUploadingFoto] = useState<boolean>(false);
  const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);

  const handleProfilePictureUpload = async (file: File) => {
    if (!file || !file.type.startsWith('image/')) {
      toast.warning('Por favor, envie um arquivo de imagem válido.');
      return;
    }

    setIsUploadingFoto(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64String = reader.result as string;

        const adminToken = localStorage.getItem('ac_token');
        let simulatedToken = '';
        const sessionsStr = localStorage.getItem('ac_sessions');
        if (sessionsStr) {
          try {
            const sessions = JSON.parse(sessionsStr);
            if (actorId && sessions[actorId]) {
              simulatedToken = sessions[actorId].access_token;
            } else if (device === 'PROFESSOR' && sessions['PROFESSOR']) {
              simulatedToken = sessions['PROFESSOR'].access_token;
            } else if (device === 'RESPONSAVEL' && sessions['RESPONSAVEL']) {
              simulatedToken = sessions['RESPONSAVEL'].access_token;
            } else if (device === 'ALUNO' && sessions['ALUNO']) {
              simulatedToken = sessions['ALUNO'].access_token;
            }
          } catch {}
        }

        let url = '';
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };

        const targetUserId = simulatedUser?.id || actorId;
        if (!targetUserId || targetUserId === 'undefined') {
          toast.warning('Nenhum usuário simulado ativo. Conecte/faça login primeiro!');
          setIsUploadingFoto(false);
          return;
        }

        if (device === 'ALUNO') {
          url = `/api/v1/upload/foto-aluno/${targetUserId}`;
          if (adminToken) {
            headers['Authorization'] = `Bearer ${adminToken}`;
          }
        } else {
          url = `/api/v1/upload/foto-usuario/${targetUserId}`;
          if (simulatedToken) {
            headers['Authorization'] = `Bearer ${simulatedToken}`;
          } else if (adminToken) {
            headers['Authorization'] = `Bearer ${adminToken}`;
          }
        }

        const res = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({ fotoBase64: base64String })
        });
        const data = await res.json();
        if (data.success) {
          if (sessionsStr) {
            try {
              const sessions = JSON.parse(sessionsStr);
              const targetKey = (actorId && sessions[actorId]) ? actorId : device;
              if (sessions[targetKey] && sessions[targetKey].user) {
                sessions[targetKey].user.fotoUrl = data.url;
                localStorage.setItem('ac_sessions', JSON.stringify(sessions));
              }
            } catch {}
          }
          setProfileUpdateKey(prev => prev + 1);
          setIframeReloadKey(prev => prev + 1);
          toast.success('Foto de perfil atualizada!');
        } else {
          toast.error('Falha ao enviar imagem: ' + (data.error || data.message || 'Erro desconhecido'));
        }
        setIsUploadingFoto(false);
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      toast.error('Erro no upload: ' + err.message);
      setIsUploadingFoto(false);
    }
  };

  const takeScreenshot = async () => {
    try {
      const mediaEl = getIframeMediaElement();

      if (mediaEl) {
        const canvas = document.createElement('canvas');
        if (mediaEl instanceof HTMLVideoElement) {
          canvas.width = mediaEl.videoWidth || mediaEl.clientWidth;
          canvas.height = mediaEl.videoHeight || mediaEl.clientHeight;
        } else {
          canvas.width = (mediaEl as HTMLCanvasElement).width || mediaEl.clientWidth;
          canvas.height = (mediaEl as HTMLCanvasElement).height || mediaEl.clientHeight;
        }
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(mediaEl, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => {
            if (blob) downloadBlob(blob, `screenshot_${new Date().toISOString().replace(/[:.]/g, '-')}.png`);
          }, 'image/png');
        }
        return;
      }

      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'browser' } as any,
        audio: false,
      });
      const video = document.createElement('video');
      video.srcObject = stream;
      await video.play();
      await new Promise(resolve => setTimeout(resolve, 300));

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (blob) downloadBlob(blob, `screenshot_${new Date().toISOString().replace(/[:.]/g, '-')}.png`);
        }, 'image/png');
      }
      stream.getTracks().forEach(t => t.stop());
    } catch (err) {
      console.error('Erro ao tirar screenshot:', err);
    }
  };

  // Manipulador de carregamento do emulador (injetor de CSS e escutas de controle)
  const handleEmulatorIframeLoad = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
    try {
      const iframe = e.currentTarget;
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        // 1. Injetar a folha de estilo se não existir
        if (!doc.getElementById('custom-emulator-styles')) {
          const style = doc.createElement('style');
          style.id = 'custom-emulator-styles';
          style.innerHTML = `
            /* Esconde por padrão */
            .control-buttons-list {
              display: none !important;
            }
            /* Exibe se a classe estiver na body */
            body.show-native-controls .control-buttons-list {
              display: flex !important;
              position: absolute !important;
              right: 8px !important;
              top: 50% !important;
              transform: translateY(-50%) !important;
              z-index: 9999 !important;
              background: rgba(17, 17, 27, 0.85) !important;
              backdrop-filter: blur(8px) !important;
              border: 1px solid rgba(255, 255, 255, 0.15) !important;
              border-radius: 12px !important;
              padding: 6px !important;
              flex-direction: column !important;
              gap: 6px !important;
              box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6) !important;
            }
            .control-button {
              background: transparent !important;
              border: none !important;
              border-radius: 6px !important;
              padding: 6px !important;
              cursor: pointer !important;
              transition: all 0.2s !important;
              display: flex !important;
              justify-content: center !important;
              align-items: center !important;
            }
            .control-button:hover {
              background: rgba(255, 255, 255, 0.15) !important;
            }
            .close-toolbar-btn {
              background: #f38ba8 !important;
              color: #11111b !important;
              font-weight: bold !important;
              border-radius: 6px !important;
              width: 28px !important;
              height: 28px !important;
              font-size: 11px !important;
              border: none !important;
              cursor: pointer !important;
              margin-top: 4px !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              transition: all 0.2s !important;
            }
            .close-toolbar-btn:hover {
              background: #eba0ac !important;
              transform: scale(1.05) !important;
            }
            
            .device-view {
              width: 100% !important;
              height: 100% !important;
              display: flex !important;
              justify-content: center !important;
              align-items: center !important;
              background: #11111b !important;
              margin: 0 !important;
              padding: 0 !important;
              overflow: hidden !important;
            }
            .video {
              width: 100% !important;
              height: 100% !important;
              position: relative !important;
              display: flex !important;
              justify-content: center !important;
              align-items: center !important;
              background: #11111b !important;
            }
            .video-layer,
            .touch-layer {
              position: absolute !important;
              width: 100% !important;
              height: 100% !important;
              object-fit: fill !important;
              top: 0 !important;
              left: 0 !important;
            }
            canvas.touch-layer {
              background: transparent !important;
              opacity: 1 !important;
              pointer-events: auto !important;
            }
            html, body, #container, .video-container, .stream-container, .player-container {
              width: 100% !important;
              height: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              background: #11111b !important;
              overflow: hidden !important;
            }
          `;
          doc.head.appendChild(style);
        }

        // 2. Adicionar botão fechar no final da lista
        const addCloseButton = () => {
          const list = doc.querySelector('.control-buttons-list');
          if (list && !list.querySelector('.close-toolbar-btn')) {
            const closeBtn = doc.createElement('button');
            closeBtn.className = 'close-toolbar-btn';
            closeBtn.innerText = 'X';
            closeBtn.title = 'Fechar Painel';
            closeBtn.addEventListener('click', () => {
              setIsEmulatorSettingsOpen(false);
            });
            list.appendChild(closeBtn);
          }
        };

        addCloseButton();
        const observer = new MutationObserver(addCloseButton);
        observer.observe(doc.body, { childList: true, subtree: true });

        // Sincroniza a classe de exibição inicial
        if (isEmulatorSettingsOpen) {
          doc.body.classList.add('show-native-controls');
        } else {
          doc.body.classList.remove('show-native-controls');
        }
      }
    } catch (err) {
      console.warn("Could not inject CSS to hide ws-scrcpy controls:", err);
    }
  };

  useEffect(() => {
    if (rightViewMode !== 'EMULATOR' || !iframeRef.current) return;
    try {
      const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
      if (doc) {
        if (isEmulatorSettingsOpen) {
          doc.body.classList.add('show-native-controls');
        } else {
          doc.body.classList.remove('show-native-controls');
        }
      }
    } catch {}
  }, [isEmulatorSettingsOpen, rightViewMode]);

  // No PWA, limpa overlay de digitação (exclusivo do EMU)
  useEffect(() => {
    if (rightViewMode === 'WEB_PWA' || compactEmbed) {
      setActiveInput(null);
      setInputValue('');
    }
  }, [rightViewMode, compactEmbed]);

  // Polling de teclado do emulador
  useEffect(() => {
    if (rightViewMode !== 'EMULATOR' || isBackendOnline === false) return;

    let timer: ReturnType<typeof setInterval>;
    const checkGboardStatus = async () => {
      try {
        const res = await fetch(`/api/v1/dev/keyboard-status?device=${device}`);
        if (res.status === 200) {
          const data = await res.json();
          if (data.visible) {
            setActiveInput(prev => {
              if (prev) return prev;
              setInputValue('');
              return {
                id: 'emulator-gboard',
                name: 'emulator-gboard',
                label: 'Teclado Virtual do Celular',
                value: '',
                element: null as any
              };
            });
          } else {
            setActiveInput(prev => {
              if (prev && prev.id === 'emulator-gboard') return null;
              return prev;
            });
          }
        }
      } catch {}
    };

    timer = setInterval(checkGboardStatus, 1500);
    return () => clearInterval(timer);
  }, [device, rightViewMode, isBackendOnline]);

  // Listener do iframe PWA (digitação embutida é só no EMU/GBoard — no PWA digita-se no iframe)
  const handlePwaIframeLoad = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
    const iframe = e.currentTarget;
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      const win = iframe.contentWindow;
      if (!doc || !win) return;

      // Ouve erros de script dentro do iframe
      win.addEventListener('error', (errEvent: ErrorEvent) => {
        setErrorLogs(prev => [
          ...prev,
          {
            message: `[PWA Iframe] ${errEvent.message}`,
            stack: errEvent.error?.stack || 'Sem Stack Trace',
            time: new Date().toLocaleTimeString(),
            source: 'Iframe PWA'
          }
        ]);
      });
    } catch {}
  };

  // Captura erros globais do dashboard principal
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      setErrorLogs(prev => [
        ...prev,
        {
          message: event.message || 'Erro Desconhecido',
          stack: event.error?.stack || 'Sem Stack Trace',
          time: new Date().toLocaleTimeString(),
          source: 'Frontend'
        }
      ]);
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      setErrorLogs(prev => [
        ...prev,
        {
          message: event.reason?.message || String(event.reason),
          stack: event.reason?.stack || 'Sem Stack Trace',
          time: new Date().toLocaleTimeString(),
          source: 'Promise Rejeitada'
        }
      ]);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      // PWA_INPUT_FOCUS ignorado: overlay de digitação é exclusivo do modo EMU (GBoard)

      // Handshake só se o iframe pediu (impersonate_receiver); nunca reusa localStorage do outro painel
      if (event.data?.type === 'iframe_ready' && iframeRef.current && iframeRef.current.contentWindow === event.source) {
        try {
          const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
          const role = String(device).toUpperCase();
          let token = pwaAuthToken || '';
          let user: any = pwaAuthUser;

          const scoped = schoolId ? sessions[`${schoolId}::${role}`] : null;
          const byActor = effectiveActorId ? sessions[effectiveActorId] : null;
          const byRole = sessions[role];
          const hit = byActor || scoped || byRole;
          if (hit?.access_token) {
            token = hit.access_token;
            user = hit.user || user;
          }

          if (token && user && iframeRef.current?.contentWindow) {
            const perfil = String(user?.perfil || role).toUpperCase();
            iframeRef.current.contentWindow.postMessage(
              {
                type: 'impersonate_login',
                data: {
                  access_token: token,
                  user,
                  redirect_url: PHONE_PWA_REDIRECT[perfil] || PHONE_PWA_REDIRECT[role] || '/',
                  skip_splash: true,
                  from_cockpit: true,
                },
              },
              window.location.origin,
            );
          }
        } catch { /* noop */ }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [device, effectiveActorId, schoolId, pwaAuthToken, pwaAuthUser]);

  // Digitação com debounce de 1s (Idle Auto-Submit) — só usado no overlay EMU/GBoard
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInputValue(val);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      submitTextToPwaVal(val);
    }, 1000);
  };

  const submitTextToPwaVal = async (val: string) => {
    if (!activeInput) return;
    if (activeInput.id === 'emulator-gboard') {
      try {
        await sendAndroidText(val);
      } catch (err) {
        console.error('Erro ao enviar texto para o emulador:', err);
      }
    } else {
      try {
        const el = activeInput.element;
        if (el) {
          el.value = val;
          el.dispatchEvent(new Event('input', { bubbles: true }));
          el.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } catch (err) {
        console.error('Erro ao enviar texto para o input do PWA:', err);
      }
    }
  };

  const submitTextToPwa = async () => {
    if (!activeInput) return;
    await submitTextToPwaVal(inputValue);

    if (activeInput.id === 'emulator-gboard') {
      try {
        await fetch('/api/v1/dev/adb', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ device, command: 'input_keyevent', keyevent: 66 })
        });
      } catch {}
    } else {
      try {
        if (activeInput.element) activeInput.element.blur();
      } catch {}
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    setActiveInput(null);
  };

  const cancelPwaInput = () => {
    if (activeInput && activeInput.id !== 'emulator-gboard') {
      try { activeInput.element.blur(); } catch {}
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    setActiveInput(null);
  };

  // Mouse drag overlay triggers (desativados temporariamente)
  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    // setShowDragOverlay(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      // setShowDragOverlay(true);
    }, 3000);
  };

  // --- Lógica de GPS / Geofencing ---
  const applyGpsLocation = async () => {
    if (!locationResult) return;
    try {
      await fetch('/api/v1/dev/adb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device,
          command: 'set_gps',
          lat: locationResult.lat.toString(),
          lng: locationResult.lon.toString()
        })
      });

      // Salva endereço recente
      setSavedAddresses(prev => {
        const exists = prev.some(x => x.display_name === locationResult.display_name);
        if (exists) return prev;
        const updated = [locationResult, ...prev].slice(0, 15);
        localStorage.setItem(`emulator_saved_addresses_${device}`, JSON.stringify(updated));
        return updated;
      });

      // Opcional: recarrega iframe se for PWA para forçar leitura de localização
      if (rightViewMode === 'WEB_PWA') {
        setIframeReloadKey(prev => prev + 1);
      }
      setIsLocationModalOpen(false);
    } catch (err) {
      console.error('Erro ao definir localização GPS:', err);
    }
  };

  const getCurrentLocation = () => {
    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocationResult({
          display_name: 'Minha Localização Física Atual',
          lat: pos.coords.latitude,
          lon: pos.coords.longitude
        });
        setIsGettingLocation(false);
      },
      () => {
        setIsGettingLocation(false);
        toast.error('Não foi possível obter sua geolocalização física.');
      }
    );
  };

  const searchAddress = async () => {
    if (!addressQuery) return;
    setIsSearchingLocation(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressQuery)}`);
      const data = await res.json();
      if (data && data.length > 0) {
        const item = {
          display_name: data[0].display_name,
          lat: parseFloat(data[0].lat),
          lon: parseFloat(data[0].lon)
        };
        setLocationResult(item);

        if (isRouteMode) {
          setWaypoints(prev => {
            const exists = prev.some(x => x.display_name === item.display_name);
            if (exists) return prev;
            return [...prev, item];
          });
        }
      } else {
        toast.warning('Endereço não localizado. Tente digitar de outra forma.');
      }
    } catch {
      toast.error('Erro ao buscar localização.');
    } finally {
      setIsSearchingLocation(false);
    }
  };

  const selectRecentAddress = (item: { display_name: string; lat: number; lon: number }) => {
    setLocationResult(item);
    if (isRouteMode) {
      setWaypoints(prev => {
        const exists = prev.some(x => x.display_name === item.display_name);
        if (exists) return prev;
        return [...prev, item];
      });
    }
  };

  const reorderWaypoints = (fromIdx: number, toIdx: number) => {
    setWaypoints(prev => {
      const arr = [...prev];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return arr;
    });
  };

  const removeWaypoint = (idx: number) => {
    setWaypoints(prev => prev.filter((_, i) => i !== idx));
  };

  const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const formatDistance = (meters: number): string => {
    if (meters < 1000) return `${Math.round(meters)}m`;
    return `${(meters / 1000).toFixed(1)}km`;
  };

  const getOsrmRoute = async (points: Array<{ lat: number; lon: number }>): Promise<Array<[number, number]>> => {
    if (points.length < 2) return [];
    const profile = OSRM_PROFILES[routeVehicle] || 'driving';
    const coords = points.map(p => `${p.lon},${p.lat}`).join(';');
    const cacheKey = `${profile}-${coords}`;

    const cached = routeCacheRef.current[cacheKey];
    if (cached) return cached;

    try {
      const res = await fetch(`https://router.project-osrm.org/route/v1/${profile}/${coords}?overview=full&geometries=geojson`);
      const data = await res.json();
      if (data.routes && data.routes.length > 0) {
        const routePoints: Array<[number, number]> = data.routes[0].geometry.coordinates.map(
          (c: any) => [c[1], c[0]]
        );
        routeCacheRef.current[cacheKey] = routePoints;
        try { localStorage.setItem('emulator_route_cache', JSON.stringify(routeCacheRef.current)); } catch {}
        return routePoints;
      }
    } catch {}
    return [];
  };

  const startRoute = async () => {
    const routePoints = await getOsrmRoute(waypoints);
    if (routePoints.length === 0) {
      toast.error('Não foi possível calcular a rota.');
      return;
    }
    routePointsRef.current = routePoints;
    routeIndexRef.current = 0;
    setIsRouteRunning(true);

    const [startLat, startLng] = routePoints[0];
    await fetch('/api/v1/dev/adb', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        device,
        command: 'set_gps',
        lat: startLat.toString(),
        lng: startLng.toString()
      })
    });

    setRouteProgress('A · Início');
    const speedKmh = SPEED_MAP[routeSpeed] || 40;

    const tickRoute = async () => {
      const idx = routeIndexRef.current;
      if (idx >= routePointsRef.current.length - 1) {
        stopRoute();
        setRouteProgress('Rota concluída ✓');
        setTimeout(() => setRouteProgress(''), 5000);
        return;
      }

      const nextIdx = idx + 1;
      routeIndexRef.current = nextIdx;
      const [lat, lng] = routePointsRef.current[nextIdx];
      const totalPoints = routePointsRef.current.length;
      const percent = Math.round((nextIdx / (totalPoints - 1)) * 100);

      let distPercorrida = 0;
      for (let i = 1; i <= nextIdx; i++) {
        distPercorrida += haversineDistance(
          routePointsRef.current[i - 1][0], routePointsRef.current[i - 1][1],
          routePointsRef.current[i][0], routePointsRef.current[i][1]
        );
      }

      setRouteProgress(`${percent}% · ${formatDistance(distPercorrida)} · ${speedKmh}km/h`);

      try {
        await fetch('/api/v1/dev/adb', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            device,
            command: 'set_gps',
            lat: lat.toString(),
            lng: lng.toString()
          })
        });
      } catch {}
    };

    const avgDist = routePoints.length > 1
      ? haversineDistance(routePoints[0][0], routePoints[0][1], routePoints[1][0], routePoints[1][1])
      : 15;
    const intervalMs = (avgDist / (speedKmh / 3.6)) * 1000;

    routeIntervalRef.current = setInterval(tickRoute, Math.max(1200, intervalMs));
    setIsLocationModalOpen(false);
  };

  const stopRoute = () => {
    if (routeIntervalRef.current) {
      clearInterval(routeIntervalRef.current);
      routeIntervalRef.current = null;
    }
    setIsRouteRunning(false);
  };

  const simulatedUser = (() => {
    try {
      const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
      if (effectiveActorId && sessions[effectiveActorId]) {
        return sessions[effectiveActorId].user;
      }
      if (device && sessions[device]) {
        return sessions[device].user;
      }
    } catch {}
    return null;
  })();

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative flex flex-col w-full h-full max-w-full max-h-full transition-all duration-300 pt-0 px-0 pb-0 ${
        compactEmbed ? 'items-stretch justify-start' : 'items-center justify-center'
      }`}
    >
      <style>{`
        @keyframes androidShimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .android-3d-button {
          background: linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%);
          background-size: 200% auto;
          border: none;
          border-bottom: 4px solid #064e3b !important;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3) !important;
          transition: all 0.15s ease;
          position: relative;
          overflow: hidden;
        }
        .android-3d-button:hover {
          background-position: right center;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.4) !important;
        }
        .android-3d-button:active {
          border-bottom-width: 0px !important;
          transform: translateY(2px);
          box-shadow: 0 2px 6px rgba(16, 185, 129, 0.2) !important;
        }
        .android-3d-button::after {
          content: '';
          position: absolute;
          top: 0; right: 0; bottom: 0; left: 0;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
          transform: translateX(-100%);
          animation: androidShimmer 3s infinite;
          pointer-events: none;
        }
      `}</style>

      {/* Menu de ferramentas (header da coluna na janela escola) */}
      {compactEmbed && toolsMenuOpen && (
        <>
          <div
            className="absolute inset-0 z-[60]"
            onClick={closeToolsMenu}
            aria-hidden
          />
          <div
            className="absolute top-2 left-2 z-[61] w-[220px] rounded-xl border border-[#313244] bg-[#1e1e2e] shadow-2xl py-1.5 text-[#cdd6f4]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-1.5 text-[9px] font-extrabold uppercase tracking-wider text-[#89b4fa] border-b border-[#313244]/80 mb-1">
              Ferramentas · {device}
            </div>
            {([
              {
                key: 'user',
                label: 'Usuário simulado',
                onClick: () => runToolAction(() => setIsContextMenuOpen(true)),
              },
              {
                key: 'reload',
                label: 'Recarregar tela',
                onClick: () => runToolAction(() => setIframeReloadKey((prev) => prev + 1)),
              },
              {
                key: 'network',
                label: (isWifiOn && isDataOn) ? 'Rede: Wi‑Fi / Dados ON' : 'Rede: OFF',
                onClick: () => runToolAction(toggleNetwork),
              },
              {
                key: 'dark',
                label: isDarkMode ? 'Modo claro' : 'Modo escuro',
                onClick: () => runToolAction(toggleDarkMode),
              },
              {
                key: 'gps',
                label: isRouteRunning ? `GPS ativo (${routeProgress}) — Parar` : 'Simular GPS / rota',
                onClick: () =>
                  runToolAction(() => {
                    if (isRouteRunning) stopRoute();
                    else setIsLocationModalOpen(true);
                  }),
              },
              {
                key: 'shot',
                label: 'Screenshot',
                onClick: () => runToolAction(takeScreenshot),
              },
              {
                key: 'rec',
                label: isRecording ? 'Parar gravação' : 'Gravar tela',
                onClick: () => runToolAction(() => (isRecording ? stopRecording() : startRecording())),
              },
              {
                key: 'logs',
                label: errorLogs.length > 0 ? `Logs de erro (${errorLogs.length})` : 'Logs de erro',
                onClick: () => runToolAction(() => setIsErrorLogsModalOpen(true)),
              },
            ] as const).map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={item.onClick}
                className="w-full text-left px-3 py-2 text-[11px] font-semibold hover:bg-[#313244]/80 border-none bg-transparent text-[#cdd6f4] cursor-pointer"
              >
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* 1. Barra superior do celular em abas de visualização */}
      {!compactEmbed && (
      <div
        onMouseDown={onDragStart}
        onDoubleClick={onMinimize}
        className="absolute top-1.5 right-2 left-2 flex justify-between items-center bg-[#313244] rounded-t-[30px] rounded-b-none px-3 py-1.5 z-30 shadow-md cursor-grab active:cursor-grabbing select-none h-11 border-none"
      >
        {/* Lado Esquerdo: Menu e Detalhes do Usuário */}
        <div className="flex items-center gap-1.5">
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => setIsContextMenuOpen(!isContextMenuOpen)}
            className="w-6 h-6 rounded hover:bg-[#313244]/50 text-[#cdd6f4] hover:text-white flex items-center justify-center transition-all active:scale-90 cursor-pointer bg-transparent border-none shadow-none"
            title="Detalhes do Usuário Simulado"
          >
            <svg className="w-3.5 h-3.5 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          
          <div
            onMouseDown={(e) => e.stopPropagation()}
            onClick={openAuthenticatedApp}
            className="flex flex-col text-left cursor-pointer group active:scale-95 transition-all max-w-[140px]"
            title="Clique para Conectar/Abrir App no Chrome"
          >
            <span className="text-[8px] font-bold text-[#a6adc8] uppercase tracking-wider opacity-70 group-hover:text-[#89b4fa] transition-colors leading-none mb-0.5">
              {device} {isLandscape ? 'LS' : 'PT'}
            </span>
            <span className="text-[10px] font-extrabold text-white leading-tight truncate group-hover:text-[#89b4fa] transition-colors">
              {simulatedUser?.nome || simulatedUser?.name || 'Sem Simulação'}
            </span>
          </div>

        </div>

        {/* Lado Direito: Giro, Minimizar, Fechar */}
        <div className="flex items-center gap-1.5">
          {/* Giro (🔄) */}
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => {
              if (rightViewMode === 'EMULATOR') {
                toggleRotation();
              } else {
                const nextLandscape = !isLandscape;
                setIsLandscape(nextLandscape);
                onRotate?.(nextLandscape);
              }
            }}
            className="w-7 h-7 rounded-full hover:bg-[#313244]/50 text-white hover:text-white transition-all active:scale-90 cursor-pointer flex items-center justify-center bg-transparent border-none shadow-none"
            title="Girar Tela (Alternar Retrato / Paisagem)"
          >
            <svg className="w-3.5 h-3.5 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
              <path d="M21.5 2v6h-6M21.34 8a10 10 0 1 0-.5 4.5" />
              <rect x="9" y="8" width="6" height="10" rx="1.5" />
              <circle cx="12" cy="16" r="0.5" fill="currentColor" />
            </svg>
          </button>

          {/* Window Controls */}
          <div onMouseDown={(e) => e.stopPropagation()}>
            <WindowControls
              winId={device}
              isMaximized={isMaximized}
              onMinimize={onMinimize || (() => {})}
              onMaximize={onMaximize || (() => {})}
              onClose={onClose || (() => {})}
              onSnapLayout={onSnapLayout}
            />
          </div>
        </div>
      </div>
      )}

      {/* 2. Drag Overlay (Card Azul Translúcido) - Desativado temporariamente
      {showDragOverlay && (
        <div
          onMouseDown={onDragStart}
          onClick={() => setShowDragOverlay(false)}
          className="absolute inset-0 bg-[#89b4fa]/20 hover:bg-[#89b4fa]/30 transition-colors flex flex-col items-center justify-center cursor-grab active:cursor-grabbing z-40 select-none rounded-[36px]"
          title="Clique para liberar a tela, Segure para mover"
        >
          <div className="bg-[#11111b]/95 border border-[#313244] text-[#cdd6f4] font-bold text-[10px] px-3.5 py-2 rounded-xl shadow-2xl flex flex-col items-center gap-1 hover:scale-105 transition-all">
            <span className="text-base">✊</span>
            <span>Mover Janela</span>
            <span className="text-[8px] text-[#585b70] font-normal italic mt-0.5">Clique para usar o celular</span>
          </div>
        </div>
      )}
      */}

      {/* 3. Toolbar Lateral Esquerda - Desativada/Movida para a barra superior */}
      {/*
      <div className="absolute left-2 top-12 flex flex-col gap-1.5 z-30">
        <button
          onClick={() => setIframeReloadKey(prev => prev + 1)}
          className="w-6.5 h-6.5 rounded bg-[#313244] text-[#a6adc8] hover:text-[#cdd6f4] active:scale-90 flex items-center justify-center transition-all shadow border border-[#45475a] cursor-pointer"
          title="Recarregar"
        >
          <svg className="w-3 h-3 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
            <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
        </button>

        <button
          onClick={toggleNetwork}
          className="w-6.5 h-6.5 rounded text-[#a6adc8] hover:text-[#cdd6f4] active:scale-90 flex items-center justify-center transition-all shadow border border-[#45475a] cursor-pointer"
          style={{ background: (isWifiOn && isDataOn) ? 'linear-gradient(to right, #a6e3a1, #313244)' : '#313244' }}
          title="Alternar Rede"
        >
          {(isWifiOn && isDataOn) ? (
            <svg className="w-3 h-3 fill-none stroke-[#11111b] stroke-2" viewBox="0 0 24 24">
              <path d="M5 12.55a11 11 0 0 1 14.08 0M1.42 9a16 16 0 0 1 21.16 0M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01" />
            </svg>
          ) : (
            <svg className="w-3 h-3 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
              <line x1="1" y1="1" x2="23" y2="23" />
              <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55M5 12.55a10.94 10.94 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.56 9M1.42 9a15.91 15.91 0 0 1 4.7-2.88M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01" />
            </svg>
          )}
        </button>

        <button
          onClick={toggleDarkMode}
          className="w-6.5 h-6.5 rounded bg-[#313244] text-[#a6adc8] hover:text-[#cdd6f4] active:scale-90 flex items-center justify-center transition-all shadow border border-[#45475a] cursor-pointer"
          title="Dark Mode"
        >
          {isDarkMode ? (
            <svg className="w-3 h-3 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="5" />
              <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
              <line x1="1" y1="12" x2="3" y2="12" />
            </svg>
          ) : (
            <svg className="w-3 h-3 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          )}
        </button>

        <button
          onClick={isRouteRunning ? stopRoute : () => setIsLocationModalOpen(true)}
          className={`w-6.5 h-6.5 rounded bg-[#313244] active:scale-90 flex items-center justify-center transition-all shadow border border-[#45475a] cursor-pointer ${isRouteRunning ? 'text-[#a6e3a1] animate-pulse' : 'text-[#a6adc8] hover:text-[#cdd6f4]'}`}
          title={isRouteRunning ? `Rota GPS Ativa: ${routeProgress} — Parar` : 'Definir GPS'}
        >
          <svg className="w-3 h-3 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
        </button>

        <button
          onClick={takeScreenshot}
          className="w-6.5 h-6.5 rounded bg-[#313244] text-[#a6adc8] hover:text-[#cdd6f4] active:scale-90 flex items-center justify-center transition-all shadow border border-[#45475a] cursor-pointer"
          title="Tirar Screenshot"
        >
          <svg className="w-3 h-3 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
        </button>

        <button
          onClick={isRecording ? stopRecording : startRecording}
          className={`w-6.5 h-6.5 rounded active:scale-90 flex items-center justify-center transition-all shadow border cursor-pointer ${isRecording ? 'bg-[#f38ba8] border-[#f38ba8] text-[#11111b] animate-pulse' : 'bg-[#313244] border-[#45475a] text-[#a6adc8] hover:text-[#cdd6f4]'}`}
          title={isRecording ? 'Parar Gravação e Baixar Vídeo' : 'Gravar Tela'}
        >
          {isRecording ? (
            <svg className="w-2.5 h-2.5 fill-current" viewBox="0 0 24 24">
              <rect x="4" y="4" width="16" height="16" rx="1.5" />
            </svg>
          ) : (
            <svg className="w-3 h-3 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
              <path d="M23 7l-7 5 7 5V7z" />
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
            </svg>
          )}
        </button>

        <button
          onClick={() => setIsErrorLogsModalOpen(true)}
          className={`w-6.5 h-6.5 rounded active:scale-90 flex items-center justify-center transition-all shadow border cursor-pointer relative ${errorLogs.length > 0 ? 'bg-[#f9e2af] border-[#f9e2af] text-[#11111b]' : 'bg-[#313244] border-[#45475a] text-[#a6adc8] hover:text-[#cdd6f4]'}`}
          title={`Logs de Erro (${errorLogs.length})`}
        >
          <svg className="w-3 h-3 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          {errorLogs.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#f38ba8] text-white text-[7px] font-bold px-1 rounded-full border border-[#11111b] scale-90">
              {errorLogs.length > 9 ? '9+' : errorLogs.length}
            </span>
          )}
        </button>
      </div>
      */}

      {/* 4. Moldura do Celular (ou painel PWA full-bleed no embed) */}
      <div
        onMouseDown={compactEmbed ? undefined : onDragStart}
        style={{ height: '100%', width: '100%' }}
        className={
          compactEmbed
            ? 'relative overflow-hidden flex flex-col w-full h-full bg-[#181825] border-0 rounded-none shadow-none'
            : 'border-[6px] border-[#313244] bg-[#313244] rounded-[36px] shadow-[0_25px_60px_rgba(0,0,0,0.65)] relative overflow-hidden flex flex-col w-full h-full cursor-grab active:cursor-grabbing pt-11 pb-11'
        }
      >
        {/* Status Bar */}
        {!compactEmbed && rightViewMode === 'WEB_PWA' && (
          <div
            onMouseDown={(e) => e.stopPropagation()}
            className="h-7 bg-[#000] flex justify-between items-center px-6 text-[9px] text-[#e0e0e0] select-none flex-shrink-0 relative"
          >
            <span className="font-semibold text-[10px] tracking-wide text-[#cdd6f4]">{phoneTime}</span>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-[#0b0b0f] rounded-full border border-[#222330] flex items-center justify-center">
              <div className="w-1.5 h-1.5 bg-[#082218] rounded-full opacity-60"></div>
            </div>
            <div className="flex items-center space-x-1.5 text-[10px]">
              <span className="text-[8px] text-[#a6adc8] font-mono">100%</span>
            </div>
          </div>
        )}

        {/* Main Screen Content */}
        <div
          onMouseDown={(e) => e.stopPropagation()}
          className="flex-1 bg-[#181825] overflow-hidden relative min-h-0"
        >
          {isContextMenuOpen && (() => {
            const sessionsStr = localStorage.getItem('ac_sessions') || '{}';
            let availableSessions: any[] = [];
            try {
              const sessions = JSON.parse(sessionsStr);
              if (schoolId) {
                availableSessions = Object.values(sessions).filter((s: any) => {
                  if (!s?.user || !s?.access_token) return false;
                  if (s.schoolId && s.schoolId !== schoolId) return false;
                  const p = (s.user.perfil || '').toUpperCase();
                  return s.schoolId === schoolId || ['PROFESSOR', 'RESPONSAVEL', 'ALUNO', 'ESCOLA'].includes(p);
                });
                const seen = new Set<string>();
                availableSessions = availableSessions.filter((s: any) => {
                  const id = s.user?.id;
                  if (!id || seen.has(id)) return false;
                  seen.add(id);
                  return true;
                });
              } else {
                const isCelularSlot = String(device).startsWith('CELULAR');
                availableSessions = Object.values(sessions).filter((s: any) => {
                  if (!s?.user || !s?.access_token) return false;
                  if (s.user.perfil === device) return true;
                  if (device === 'PROFESSOR' && s.user.perfil === 'PROFESSOR') return true;
                  if (device === 'ALUNO' && s.user.perfil === 'ALUNO') return true;
                  if (device === 'RESPONSAVEL' && s.user.perfil === 'RESPONSAVEL') return true;
                  // Admin nos slots CELULAR1–3: lista sessões mobile já geradas
                  if (isCelularSlot) {
                    const p = (s.user.perfil || '').toUpperCase();
                    return ['PROFESSOR', 'RESPONSAVEL', 'ALUNO'].includes(p);
                  }
                  return false;
                });
                const seen = new Set<string>();
                availableSessions = availableSessions.filter((s: any) => {
                  const id = s.user?.id;
                  if (!id || seen.has(id)) return false;
                  seen.add(id);
                  return true;
                });
              }
            } catch {}

            return (
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDraggingOver(true); }}
                onDragLeave={() => setIsDraggingOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDraggingOver(false);
                  if (simulatedUser && e.dataTransfer.files[0]) {
                    handleProfilePictureUpload(e.dataTransfer.files[0]);
                  }
                }}
                className="absolute inset-0 bg-[#ffffff]/45 backdrop-blur-md z-[55] flex flex-col items-center justify-start p-5 text-[#11111b] select-text overflow-y-auto select-none"
              >
                {/* Close Button */}
                <button
                  onClick={() => setIsContextMenuOpen(false)}
                  className="absolute top-3 right-3 text-[#313244] hover:text-black font-bold text-xs bg-[#ffffff]/60 hover:bg-[#ffffff]/90 w-6.5 h-6.5 flex items-center justify-center rounded-full cursor-pointer border border-[#313244]/10 transition-all active:scale-90"
                  title="Fechar Detalhes"
                >
                  ✕
                </button>


                {/* São Paulo clock & weather premium card widget */}
                {(() => {
                  const w = getSpWeather();
                  return (
                    <div className="flex items-center gap-3 bg-[#1e1e2e]/95 text-white border border-[#313244] px-4 py-2.5 rounded-2xl w-full max-w-[210px] shadow-lg mb-3 mt-4">
                      <div className="text-xl leading-none">{w.icon}</div>
                      <div className="flex-1 flex flex-col justify-center min-w-0">
                        <div className="flex justify-between items-baseline">
                          <span className="text-[10px] font-black tracking-wide text-white leading-none">São Paulo</span>
                          <span className="text-[8px] font-bold text-[#89b4fa] leading-none uppercase">{spDate}</span>
                        </div>
                        <div className="text-xs font-black text-white leading-tight mt-1">
                          {spTime} <span className="text-[10px] font-normal text-[#a6adc8]">({w.temp})</span>
                        </div>
                        <span className="text-[7.5px] text-[#a6adc8] font-bold leading-none mt-0.5 truncate">{w.desc}</span>
                      </div>
                    </div>
                  );
                })()}

                {simulatedUser && !schoolId ? (
                  <>
                    <span className="text-[9px] font-extrabold text-[#313244] uppercase tracking-wider mb-4 opacity-80 mt-2">
                      Simulação Ativa
                    </span>

                    {/* Avatar Upload Container */}
                    <div 
                      onClick={() => {
                        const input = document.getElementById(`avatar-upload-input-${device}`);
                        if (input) input.click();
                      }}
                      className={`relative w-28 h-28 rounded-full border-4 cursor-pointer transition-all flex items-center justify-center overflow-hidden shadow-lg group ${isDraggingOver ? 'border-[#89b4fa] scale-105 bg-[#89b4fa]/20' : 'border-[#313244]/20 hover:border-[#89b4fa]'}`}
                      title="Arraste uma foto ou clique para fazer upload"
                    >
                      <input 
                        id={`avatar-upload-input-${device}`}
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            handleProfilePictureUpload(e.target.files[0]);
                          }
                        }}
                      />

                      {isUploadingFoto ? (
                        <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white">
                          <div className="w-6 h-6 rounded-full border-2 border-transparent border-t-white animate-spin mb-1" />
                          <span className="text-[8px] font-bold">Enviando...</span>
                        </div>
                      ) : (
                        <>
                          {simulatedUser.fotoUrl ? (
                            <img 
                              src={simulatedUser.fotoUrl} 
                              alt="Avatar" 
                              className="w-full h-full object-cover" 
                            />
                          ) : (
                            <div className="flex flex-col items-center justify-center text-[#313244]">
                              <svg className="w-8 h-8 opacity-75" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <span className="text-[7px] font-bold mt-1 opacity-60 uppercase">Upload</span>
                            </div>
                          )}
                          {/* Hover overlay */}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                              <circle cx="12" cy="13" r="3" />
                            </svg>
                          </div>
                        </>
                      )}
                      
                      {isDraggingOver && (
                        <div className="absolute inset-0 bg-[#89b4fa]/90 flex items-center justify-center text-white font-bold text-[9px] uppercase tracking-wide">
                          Solte aqui!
                        </div>
                      )}
                    </div>

                    {/* User Details */}
                    <h2 className="text-xs font-black text-[#11111b] mt-4 mb-0.5 text-center leading-tight">
                      {simulatedUser.nome || simulatedUser.name}
                    </h2>
                    <span className="text-[9px] text-[#313244] opacity-85 text-center font-medium truncate max-w-[220px]">
                      {simulatedUser.email || 'sem_email@aluno.ac'}
                    </span>
                    <span className="text-[8px] font-extrabold uppercase tracking-widest text-[#1e1e2e] bg-white/70 px-2 py-0.5 rounded-md mt-1 border border-black/5 shadow-sm leading-none">
                      {simulatedUser.perfil || device} {simulatedUser.cargo ? `(${simulatedUser.cargo})` : ''}
                    </span>
                    {simulatedUser.nomeEscola && (
                      <span className="text-[8px] font-bold text-[#1a5c37] bg-green-100/50 px-2 py-0.5 rounded-md mt-1 border border-green-200/20 text-center leading-normal max-w-[200px]">
                        🏫 {simulatedUser.nomeEscola}
                      </span>
                    )}

                    {/* Quick Connection QR Code */}
                    {(() => {
                      const adminToken = localStorage.getItem('ac_token');
                      let token = '';
                      const sessionsStr = localStorage.getItem('ac_sessions');
                      if (sessionsStr) {
                        try {
                          const sessions = JSON.parse(sessionsStr);
                          if (actorId && sessions[actorId]) {
                            token = sessions[actorId].access_token;
                          } else if (device === 'PROFESSOR' && sessions['PROFESSOR']) {
                            token = sessions['PROFESSOR'].access_token;
                          } else if (device === 'RESPONSAVEL' && sessions['RESPONSAVEL']) {
                            token = sessions['RESPONSAVEL'].access_token;
                          } else if (device === 'ALUNO' && sessions['ALUNO']) {
                            token = sessions['ALUNO'].access_token;
                          }
                        } catch {}
                      }
                      if (!token && adminToken) {
                        token = adminToken;
                      }
                      const qrCodeUrl = `${window.location.origin}/login?autoLoginToken=${token}&promptNotification=1`;

                      return (
                        <div className="flex flex-col items-center bg-white/70 p-2.5 rounded-2xl shadow-md border border-white/40 mt-4 w-full max-w-[190px]">
                          <span className="text-[8px] font-extrabold text-[#313244] uppercase tracking-wide mb-1 text-center leading-none">
                            Ligação Rápida (PWA)
                          </span>
                          <div className="w-[130px] h-[130px] flex items-center justify-center overflow-hidden bg-white rounded-xl p-1 border border-black/5 shadow-inner">
                            {token ? (
                              <StyledQrCode value={qrCodeUrl} size={120} />
                            ) : (
                              <span className="text-[9px] text-gray-400">Sem Sessão Ativa</span>
                            )}
                          </div>
                          <span className="text-[6.5px] text-[#585b70] text-center leading-normal mt-1.5 px-0.5 font-medium">
                            Escaneie para logar e ativar notificações neste aparelho
                          </span>
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <>
                    <span className="text-[10px] font-extrabold text-[#313244] uppercase tracking-wider mb-3 opacity-90 mt-2">
                      {schoolId ? 'Usuários desta escola' : `Conectar Simulação (${device})`}
                    </span>
                    <div className="flex flex-col gap-2 w-full flex-1 min-h-0 overflow-y-auto px-1 mt-2 mb-2 pb-6">
                      {schoolId ? (
                        schoolActorsLoading ? (
                          <div className="text-center p-5 text-[10px] text-[#585b70] font-bold">Carregando usuários…</div>
                        ) : schoolActors.length === 0 ? (
                          <div className="text-center p-5 bg-white/40 rounded-2xl border border-black/5 text-[10px] text-[#585b70] font-bold leading-relaxed shadow-inner">
                            Nenhum usuário mobile nesta escola.
                          </div>
                        ) : (
                          schoolActors.map((actor) => (
                            <button
                              key={actor.id}
                              type="button"
                              disabled={connectingActorId === actor.id}
                              onClick={() => { void connectSchoolActor(actor); }}
                              className="w-full flex items-center gap-3 p-3 bg-white/60 hover:bg-[#89b4fa]/20 border border-black/5 hover:border-[#89b4fa]/30 rounded-2xl shadow-sm text-left transition-all active:scale-98 cursor-pointer select-none disabled:opacity-60"
                            >
                              <div className="w-9 h-9 rounded-full border border-black/10 overflow-hidden flex-shrink-0 bg-[#e0e0e0] flex items-center justify-center text-[11px] font-black text-[#585b70]">
                                {actor.fotoUrl ? (
                                  <img src={actor.fotoUrl} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  (actor.nome || '?').charAt(0).toUpperCase()
                                )}
                              </div>
                              <div className="flex-1 min-w-0 flex flex-col justify-center">
                                <span className="text-[10px] font-black text-[#11111b] truncate leading-tight mb-0.5">
                                  {connectingActorId === actor.id ? 'Conectando…' : actor.nome}
                                </span>
                                <span className="text-[9px] text-[#585b70] truncate leading-none opacity-85">
                                  {[actor.perfil, actor.cargo].filter(Boolean).join(' · ') || actor.email || 'sem e-mail'}
                                </span>
                              </div>
                            </button>
                          ))
                        )
                      ) : availableSessions.length === 0 ? (
                        isAdmin ? (
                          <>
                            <div className="text-[8px] font-bold text-[#585b70] uppercase tracking-wider px-1 mb-1">
                              Usuários fixos · bypass + notificação
                            </div>
                            {ADMIN_FIXED_MOBILE_USERS.map((user) => (
                              <button
                                key={user.id}
                                type="button"
                                disabled={connectingActorId === user.id}
                                onClick={() => { void connectFixedBypassUser(user); }}
                                className="w-full flex items-center gap-3 p-3 bg-white/60 hover:bg-[#a6e3a1]/25 border border-black/5 hover:border-[#a6e3a1]/40 rounded-2xl shadow-sm text-left transition-all active:scale-98 cursor-pointer select-none disabled:opacity-60"
                              >
                                <div
                                  className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-black text-white"
                                  style={{ background: user.color }}
                                >
                                  {user.avatar}
                                </div>
                                <div className="flex-1 min-w-0 flex flex-col justify-center">
                                  <span className="text-[10px] font-black text-[#11111b] truncate leading-tight mb-0.5">
                                    {connectingActorId === user.id ? 'Conectando…' : user.name}
                                  </span>
                                  <span className="text-[9px] text-[#585b70] truncate leading-none opacity-85">
                                    {user.role} · {user.email}
                                  </span>
                                </div>
                              </button>
                            ))}
                          </>
                        ) : (
                          <div className="text-center p-5 bg-white/40 rounded-2xl border border-black/5 text-[10px] text-[#585b70] font-bold leading-relaxed shadow-inner">
                            Nenhum login simulado registrado.<br/>
                            Faça login no painel para registrar sessões de teste locais.
                          </div>
                        )
                      ) : (
                        availableSessions.map((session: any, idx: number) => {
                          const u = session.user;
                          const tok = session.access_token;
                          return (
                            <button
                              key={idx}
                              onClick={() => {
                                openAuthenticatedApp(tok);
                                setIsContextMenuOpen(false);
                              }}
                              className="w-full flex items-center gap-3 p-3 bg-white/60 hover:bg-[#89b4fa]/20 border border-black/5 hover:border-[#89b4fa]/30 rounded-2xl shadow-sm text-left transition-all active:scale-98 cursor-pointer select-none"
                            >
                              <div className="w-9 h-9 rounded-full border border-black/10 overflow-hidden flex-shrink-0 bg-[#e0e0e0] flex items-center justify-center">
                                {u.fotoUrl ? (
                                  <img src={u.fotoUrl} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <svg className="w-4 h-4 text-[#585b70]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                )}
                              </div>
                              <div className="flex-1 min-w-0 flex flex-col justify-center">
                                <span className="text-[10px] font-black text-[#11111b] truncate leading-tight mb-0.5">{u.nome || u.name}</span>
                                <span className="text-[9px] text-[#585b70] truncate leading-none opacity-85">{u.email || 'sem_email@aluno.ac'}</span>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })()}
           {showSlowBootWarning && rightViewMode === 'EMULATOR' && (
            <div className="absolute top-2 left-2 right-2 bg-[#f9e2af] text-[#11111b] text-[10px] font-semibold p-2.5 rounded-xl shadow-xl z-50 flex items-start gap-2 border border-[#f9e2af]/50">
              <svg className="w-4 h-4 text-[#11111b] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="leading-tight text-left">
                É necessário fazer o primeiro acesso manualmente caso o dispositivo demore a responder.
              </div>
            </div>
          )}

          {!isBackendOnline ? (
            <div
              onMouseDown={onDragStart}
              onDoubleClick={onMinimize}
              className="absolute inset-0 bg-[#11111b]/95 backdrop-blur-md flex flex-col justify-center items-center text-center p-6 select-none z-50 cursor-grab active:cursor-grabbing"
            >
              <div onMouseDown={(e) => e.stopPropagation()} className="flex flex-col items-center select-none">
                <div className="w-12 h-12 rounded-full border-4 border-[#313244] border-t-[#a6e3a1] animate-spin mb-4" />
                <h3 className="text-sm font-bold text-[#cdd6f4] mb-1">Servidor Offline</h3>
                <p className="text-[10px] text-[#a6adc8]">Aguardando conexão...</p>
              </div>
            </div>
          ) : rightViewMode === 'EMULATOR' ? (
            isDeviceOnline ? (
              <iframe
                id={`iframe-emu-${device}`}
                name={`emu-${device}`}
                key={iframeReloadKey}
                ref={iframeRef}
                src={getEmulatorUrl()}
                title={`Emulator ${device}`}
                onLoad={handleEmulatorIframeLoad}
                style={{ pointerEvents: 'auto' }}
                className="w-full h-full border-none"
              />
            ) : (
              <div
                onMouseDown={onDragStart}
                onDoubleClick={onMinimize}
                className="w-full h-full flex flex-col justify-center items-center bg-[#11111b] text-center p-6 select-none relative cursor-grab active:cursor-grabbing"
              >
                <div onMouseDown={(e) => e.stopPropagation()} className="flex flex-col items-center w-full">
                  <div className="w-12 h-12 rounded-full border-4 border-[#313244] border-t-[#89b4fa] animate-spin mb-4" />
                  
                  <h3 className="text-sm font-bold text-[#cdd6f4] mb-1">
                    {isStartingDevice ? (bootStatusMessage || "Iniciando Emulador...") : "Celular Offline"}
                  </h3>
                  
                  <p className="text-[10px] text-[#a6adc8] mb-4">
                    Tempo decorrido: <span className="font-bold text-[#89b4fa]">{elapsedSeconds}s</span> / 15s
                  </p>

                  {!isStartingDevice && (
                    <button
                      onMouseDown={(e) => e.stopPropagation()}
                      onDoubleClick={(e) => e.stopPropagation()}
                      onClick={startDevice}
                      className="android-3d-button flex items-center justify-center px-6 py-3.5 text-xs font-black rounded-2xl text-white transition-all cursor-pointer active:scale-95 border-none mb-4"
                    >
                      <svg className="w-4 h-4 text-white mr-2 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.5 10c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1s1-.45 1-1v-6c0-.55-.45-1-1-1zm-11 0c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1s1-.45 1-1v-6c0-.55-.45-1-1-1zm1.5-4.22l-1.35-1.35c-.2-.2-.51-.2-.71 0a.495.495 0 000 .71L7.82 6.5C6.09 7.74 5 9.74 5 12h14c0-2.26-1.09-4.26-2.82-5.5l1.38-1.36c.2-.2.2-.51 0-.71a.495.495 0 00-.71 0L15 5.78C14.12 5.28 13.09 5 12 5s-2.12.28-3 .78zM9.5 9c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm5 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" />
                      </svg>
                      Ligar Celular Android
                    </button>
                  )}

                  {elapsedSeconds >= 6 && (
                    <div
                      onMouseDown={(e) => e.stopPropagation()}
                      onDoubleClick={(e) => e.stopPropagation()}
                      className="absolute bottom-6 left-6 right-6 bg-[#1e1e2e]/98 border border-[#313244] rounded-2xl p-4 shadow-2xl text-left pointer-events-auto select-text z-40"
                    >
                      <p className="text-[11.5px] text-[#cdd6f4] leading-relaxed m-0 mb-3 font-semibold">
                        💡 A inicialização está demorando. Deseja usar o modo PWA para não ficar parado?
                      </p>
                      <button
                        onClick={() => {
                          setRightViewMode('WEB_PWA');
                          setElapsedSeconds(0);
                        }}
                        className="w-full py-2.5 text-[11.5px] font-extrabold rounded-xl bg-[#b4befe] hover:bg-[#cba6f7] text-[#11111b] transition-all cursor-pointer border-none shadow-md"
                      >
                        Usar Modo PWA
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          ) : (
            <iframe
              id={`iframe-pwa-${device}`}
              name={`pwa-${device}`}
              key={iframeReloadKey}
              ref={iframeRef}
              src={
                pwaAuthToken
                  ? buildPhoneImpersonateSrc(pwaAuthToken, pwaAuthUser, device, { promptNotification: true })
                  : (initialUrl || `${window.location.origin}/login?impersonate_receiver=true`)
              }
              title={`Iframe PWA ${device}`}
              onLoad={handlePwaIframeLoad}
              className="w-full h-full border-none"
            />
          )}

          {/* Revert Prompt Banner */}
          {showRevertPrompt && rightViewMode === 'WEB_PWA' && (
            <div className="absolute top-2 left-2 right-2 bg-[#11111b]/95 border border-[#313244] rounded-xl p-3 shadow-2xl z-40 flex flex-col gap-2 animate-fade-in backdrop-blur-md">
              <div className="text-[10px] font-bold text-[#f38ba8] flex items-center gap-1.5 uppercase">
                ⚠️ Conexão do Emulador
              </div>
              <p className="text-[9px] text-[#cdd6f4] leading-snug m-0 text-left">
                {isDeviceOnline 
                  ? "O Emulador Real do Android voltou a ficar online! Deseja alternar para ele agora?"
                  : "O Emulador falhou nesta sessão. Iniciamos no modo PWA para você não ficar parado. Deseja tentar o Emulador Real de novo?"
                }
              </p>
              <div className="flex gap-2 justify-end mt-1">
                <button
                  onClick={() => {
                    setShowRevertPrompt(false);
                    setHasPromptedRevert(true);
                  }}
                  className="px-2.5 py-1 rounded bg-[#313244] hover:bg-[#45475a] text-[#a6adc8] text-[9px] font-bold border-none cursor-pointer"
                >
                  Ignorar
                </button>
                <button
                  onClick={() => {
                    setShowRevertPrompt(false);
                    setHasPromptedRevert(false);
                    sessionStorage.removeItem(`ac_emulator_failed_${device}`);
                    setRightViewMode('EMULATOR');
                    hasAutoStartedRef.current = false;
                    setElapsedSeconds(0);
                  }}
                  className="px-2.5 py-1 rounded bg-[#89b4fa] hover:bg-[#b4befe] text-[#11111b] text-[9px] font-bold border-none cursor-pointer"
                >
                  {isDeviceOnline ? "Conectar Emulador" : "Tentar de Novo"}
                </button>
              </div>
            </div>
          )}

          {/* Overlay de Digitação — só EMU (GBoard); no PWA o teclado é o do próprio iframe */}
          {activeInput && rightViewMode === 'EMULATOR' && (
            <div className="absolute bottom-12 left-3 right-3 bg-[#1e1e2e]/95 border border-[#313244] rounded-xl shadow-2xl p-2.5 flex flex-col gap-2 z-40 animate-slide-up backdrop-blur-sm">
              <div className="flex justify-between items-center border-b border-[#313244] pb-1.5">
                <span className="text-[9px] font-bold text-[#89b4fa] uppercase tracking-wide">
                  📝 {activeInput.label}
                </span>
                <button
                  onClick={cancelPwaInput}
                  title="Sair da Digitação"
                  className="text-[#a6adc8] hover:text-[#f38ba8] active:scale-75 transition-transform bg-transparent border-none cursor-pointer"
                >
                  <svg className="w-3.5 h-3.5 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                  </svg>
                </button>
              </div>

              <div className="relative">
                <textarea
                  rows={3}
                  value={inputValue}
                  onChange={handleTextareaChange}
                  placeholder="Digite aqui..."
                  className="w-full bg-[#181825] border border-[#313244] rounded-lg p-2 text-[10px] text-[#cdd6f4] placeholder-[#585b70] focus:outline-none focus:border-[#89b4fa] resize-none pr-7"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.ctrlKey) {
                      e.preventDefault();
                      submitTextToPwa();
                    } else if (e.key === 'Enter' && e.ctrlKey) {
                      setInputValue(prev => prev + '\n');
                    } else if (e.key === 'Escape') {
                      cancelPwaInput();
                    }
                  }}
                  autoFocus
                />
                {inputValue && (
                  <button
                    onClick={() => {
                      setInputValue('');
                      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                    }}
                    title="Limpar Texto"
                    className="absolute right-2 top-2 w-4.5 h-4.5 rounded-full bg-[#313244] hover:bg-[#45475a] text-[#a6adc8] hover:text-white flex items-center justify-center text-[9px] font-bold active:scale-75 transition-all border-none cursor-pointer"
                  >
                    ✕
                  </button>
                )}
              </div>
              
              <div className="flex justify-between items-center text-[8px] text-[#585b70]">
                <span>Pressione Enter para concluir</span>
                <span className="italic">Auto-envio após 1s</span>
              </div>
            </div>
          )}
        </div>

        {/* Rodapé de Navegação */}
        {!compactEmbed && rightViewMode === 'WEB_PWA' && (
          <div onMouseDown={(e) => e.stopPropagation()} className="w-full">
            {navigationMode === '3BUTTON' ? (
              <div className="h-10 bg-[#000] flex justify-around items-center px-10 flex-shrink-0 select-none border-t border-[#1e1e2e]/20">
                <button
                  onClick={() => sendAndroidKey('Escape', 'Escape', 27)}
                  className="w-12 h-full flex items-center justify-center text-[#a6adc8] hover:text-[#89b4fa] active:scale-75 transition-transform bg-transparent border-none cursor-pointer"
                  title="Voltar"
                >
                  <svg className="w-3 h-3 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <button
                  onClick={() => sendAndroidKey('Home', 'Home', 36)}
                  className="w-12 h-full flex items-center justify-center text-[#a6adc8] hover:text-[#89b4fa] active:scale-75 transition-transform bg-transparent border-none cursor-pointer"
                  title="Home"
                >
                  <svg className="w-4 h-4 stroke-current fill-none stroke-[2.5]" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="8" className="stroke-current fill-none stroke-2" />
                  </svg>
                </button>
                <button
                  onClick={() => sendAndroidKey('PageUp', 'PageUp', 33)}
                  className="w-12 h-full flex items-center justify-center text-[#a6adc8] hover:text-[#89b4fa] active:scale-75 transition-transform bg-transparent border-none cursor-pointer"
                  title="Recentes"
                >
                  <svg className="w-3.5 h-3.5 stroke-current fill-none stroke-[2.5]" viewBox="0 0 24 24">
                    <rect x="5" y="5" width="14" height="14" rx="2" className="stroke-current fill-none stroke-2" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="h-6 bg-[#000] flex justify-center items-center flex-shrink-0">
                <div
                  onClick={() => sendAndroidKey('Home', 'Home', 36)}
                  className="w-20 h-1 bg-[#a6adc8] hover:bg-[#89b4fa] rounded-full cursor-pointer transition-colors"
                  title="Início"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* 5. Barra inferior (Footer) para as ações do desenvolvedor */}
      {!compactEmbed && (
      <div
        onMouseDown={onDragStart}
        onDoubleClick={onMinimize}
        className="absolute bottom-1.5 right-2 left-2 flex justify-center items-center bg-[#313244] rounded-t-none rounded-b-[30px] px-3 py-1.5 z-30 shadow-md cursor-grab active:cursor-grabbing select-none h-11 border-none"
      >
        <div className="flex items-center gap-3">
          {/* Recarregar */}
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => setIframeReloadKey(prev => prev + 1)}
            className="w-6 h-6 rounded-full hover:bg-[#313244]/50 flex items-center justify-center text-white hover:text-white transition-all cursor-pointer bg-transparent border-none shadow-none active:scale-90"
            title="Recarregar Tela"
          >
            <svg className="w-3.5 h-3.5 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
              <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>

          {/* Rede */}
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={toggleNetwork}
            className="w-6 h-6 rounded-full hover:bg-[#313244]/50 flex items-center justify-center text-white hover:text-white transition-all cursor-pointer bg-transparent border-none shadow-none active:scale-90"
            title="Alternar Rede (Wi-Fi / Dados)"
          >
            {(isWifiOn && isDataOn) ? (
              <svg className="w-3.5 h-3.5 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
                <path d="M5 12.55a11 11 0 0 1 14.08 0M1.42 9a16 16 0 0 1 21.16 0M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5 fill-none stroke-[#f38ba8] stroke-2" viewBox="0 0 24 24">
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55M5 12.55a10.94 10.94 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.56 9M1.42 9a15.91 15.91 0 0 1 4.7-2.88M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01" />
              </svg>
            )}
          </button>

          {/* Modo Escuro */}
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={toggleDarkMode}
            className="w-6 h-6 rounded-full hover:bg-[#313244]/50 flex items-center justify-center text-white hover:text-white transition-all cursor-pointer bg-transparent border-none shadow-none active:scale-90"
            title="Alternar Modo Escuro"
          >
            {isDarkMode ? (
              <svg className="w-3.5 h-3.5 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                <line x1="1" y1="12" x2="3" y2="12" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </button>

          {/* GPS */}
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={isRouteRunning ? stopRoute : () => setIsLocationModalOpen(true)}
            className={`w-6 h-6 rounded-full hover:bg-[#313244]/50 flex items-center justify-center transition-all cursor-pointer bg-transparent border-none shadow-none active:scale-90 ${isRouteRunning ? 'text-[#a6e3a1] animate-pulse' : 'text-white hover:text-white'}`}
            title={isRouteRunning ? `Rota GPS Ativa: ${routeProgress} — Parar` : 'Simular Rota e Localização GPS'}
          >
            <svg className="w-3.5 h-3.5 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </button>

          {/* Screenshot */}
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={takeScreenshot}
            className="w-6 h-6 rounded-full hover:bg-[#313244]/50 flex items-center justify-center text-white hover:text-white transition-all cursor-pointer bg-transparent border-none shadow-none active:scale-90"
            title="Tirar Screenshot da Tela"
          >
            <svg className="w-3.5 h-3.5 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          </button>

          {/* Gravar Tela */}
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={isRecording ? stopRecording : startRecording}
            className={`w-6 h-6 rounded-full hover:bg-[#313244]/50 flex items-center justify-center transition-all cursor-pointer bg-transparent border-none shadow-none active:scale-90 ${isRecording ? 'text-[#f38ba8] animate-pulse' : 'text-white hover:text-white'}`}
            title={isRecording ? 'Parar Gravação e Baixar Vídeo' : 'Gravar Tela (Iniciar/Parar)'}
          >
            {isRecording ? (
              <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24">
                <rect x="4" y="4" width="16" height="16" rx="1.5" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
                <path d="M23 7l-7 5 7 5V7z" />
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
              </svg>
            )}
          </button>

          {/* Logs de Erro */}
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => setIsErrorLogsModalOpen(true)}
            className={`w-6 h-6 rounded-full hover:bg-[#313244]/50 flex items-center justify-center transition-all cursor-pointer relative bg-transparent border-none shadow-none active:scale-90 ${errorLogs.length > 0 ? 'text-[#f9e2af]' : 'text-white hover:text-white'}`}
            title={errorLogs.length > 0 ? `Exibir Logs de Erros (${errorLogs.length})` : 'Exibir Logs de Erros'}
          >
            <svg className="w-3.5 h-3.5 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            {errorLogs.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#f38ba8] text-white text-[7px] font-bold px-1 rounded-full scale-90">
                {errorLogs.length}
              </span>
            )}
          </button>

        </div>
      </div>
      )}

      {/* --- Modal do Spoofing de Localização / Roteamento GPS --- */}
      {isLocationModalOpen && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#1e1e2e] border border-[#313244] w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="bg-[#181825] px-6 py-4 border-b border-[#313244] flex justify-between items-center">
              <h3 className="text-sm font-bold text-[#89b4fa] flex items-center gap-2">
                📍 Simulador de Rota & Geolocalização GPS ({device})
              </h3>
              <button
                onClick={() => setIsLocationModalOpen(false)}
                className="text-[#a6adc8] hover:text-white text-xs font-bold px-3 py-1 rounded bg-[#313244] border-none cursor-pointer"
              >
                Fechar
              </button>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto flex-1 flex flex-col gap-4">
              {/* Barra de Busca de Endereço */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={addressQuery}
                  onChange={(e) => setAddressQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchAddress()}
                  placeholder="Pesquise por rua, número, cidade ou CEP..."
                  className="flex-1 bg-[#181825] border border-[#313244] rounded-lg px-4 py-2 text-xs text-[#cdd6f4] placeholder-[#585b70] focus:outline-none focus:border-[#89b4fa]"
                />
                <button
                  onClick={searchAddress}
                  disabled={isSearchingLocation}
                  className="px-4 py-2 text-xs font-bold rounded-lg bg-[#89b4fa] hover:bg-[#b4befe] text-[#11111b] border-none cursor-pointer active:scale-95 transition-all"
                >
                  {isSearchingLocation ? "Buscando..." : "Buscar"}
                </button>
                <button
                  onClick={getCurrentLocation}
                  disabled={isGettingLocation}
                  className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-[#a6e3a1] hover:bg-[#94e2d5] text-[#11111b] cursor-pointer disabled:opacity-50 border-none active:scale-95 transition-all"
                  title="Usar minha localização atual"
                >
                  {isGettingLocation ? (
                    <div className="w-4 h-4 rounded-full border-2 border-[#11111b] border-t-transparent animate-spin" />
                  ) : (
                    "🎯"
                  )}
                </button>
              </div>

              {/* Informações da Busca e Mapa */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Detalhes do Endereço */}
                <div className="flex flex-col justify-between bg-[#181825] p-4 rounded-xl border border-[#313244]">
                  <div>
                    <h4 className="text-xs font-bold text-[#cdd6f4] mb-2 uppercase tracking-wide">Informações do Endereço</h4>
                    {locationResult ? (
                      <div className="text-[11px] text-[#a6adc8] space-y-2 leading-relaxed">
                        <p><strong>Descrição:</strong> {locationResult.display_name}</p>
                        <p><strong>Latitude:</strong> {locationResult.lat}</p>
                        <p><strong>Longitude:</strong> {locationResult.lon}</p>
                      </div>
                    ) : (
                      <p className="text-[11px] text-[#585b70]">Nenhum endereço pesquisado. Digite acima para iniciar.</p>
                    )}
                  </div>

                  {locationResult && (
                    <button
                      onClick={isRouteMode && waypoints.length >= 2 ? startRoute : applyGpsLocation}
                      className={`mt-4 w-full py-2.5 text-xs font-bold text-center rounded-lg cursor-pointer transition-colors border-none active:scale-95 ${
                        isRouteMode && waypoints.length >= 2
                          ? 'bg-[#f9e2af] hover:bg-[#fab387] text-[#11111b]'
                          : 'bg-[#a6e3a1] hover:bg-[#89b4fa] text-[#11111b]'
                      }`}
                    >
                      {isRouteMode && waypoints.length >= 2 ? '▶ INICIAR ROTA' : 'Aplicar Localização no Emulador'}
                    </button>
                  )}
                </div>

                {/* Mapa Embed */}
                <div className="rounded-xl overflow-hidden border border-[#313244] bg-[#181825] flex items-center justify-center min-h-[220px]">
                  {locationResult ? (
                    <iframe
                      title="OpenStreetMap"
                      width="100%"
                      height="220"
                      style={{ border: 'none' }}
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${locationResult.lon - 0.003}%2C${locationResult.lat - 0.003}%2C${locationResult.lon + 0.003}%2C${locationResult.lat + 0.003}&layer=mapnik&marker=${locationResult.lat}%2C${locationResult.lon}`}
                    />
                  ) : (
                    <div className="text-[11px] text-[#585b70] text-center p-4">
                      O mapa será exibido após a pesquisa de endereço.
                    </div>
                  )}
                </div>
              </div>

              {/* Roteiro Config */}
              <div className="flex justify-between items-center">
                <h4 className="text-xs font-bold text-[#cdd6f4] uppercase tracking-wide">Pesquisas Recentes</h4>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <span className="text-[10px] text-[#a6adc8] font-semibold uppercase">Ativa Roteiro</span>
                  <div
                    onClick={() => setIsRouteMode(prev => !prev)}
                    className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${isRouteMode ? 'bg-[#a6e3a1]' : 'bg-[#45475a]'}`}
                  >
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${isRouteMode ? 'translate-x-4' : ''}`} />
                  </div>
                </label>
              </div>

              {isRouteMode && (
                <div className="bg-[#181825] border border-[#313244] rounded-xl p-4 flex flex-col gap-3">
                  <div>
                    <span className="text-[10px] text-[#a6adc8] font-bold uppercase tracking-wide">Velocidade</span>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {Object.entries(SPEED_MAP).map(([key, val]) => (
                        <button
                          key={key}
                          onClick={() => setRouteSpeed(key)}
                          className={`px-2.5 py-1 text-[9px] font-bold rounded-md border cursor-pointer transition-all active:scale-95 ${
                            routeSpeed === key ? 'bg-[#89b4fa] text-[#11111b] border-[#89b4fa]' : 'bg-transparent text-[#a6adc8] border-[#45475a] hover:border-[#89b4fa]'
                          }`}
                        >
                          {key.replace(/_/g, ' ')} ({val}km/h)
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="text-[10px] text-[#a6adc8] font-bold uppercase tracking-wide">Veículo</span>
                    <div className="flex gap-2 mt-1.5">
                      {[
                        { key: 'CARRO', icon: '🚗', label: 'Carro' },
                        { key: 'MOTO', icon: '🏍️', label: 'Moto' },
                        { key: 'BICICLETA', icon: '🚲', label: 'Bicicleta' },
                        { key: 'A_PE', icon: '🚶', label: 'A Pé' },
                      ].map(v => (
                        <button
                          key={v.key}
                          onClick={() => setRouteVehicle(v.key)}
                          className={`flex items-center gap-1 px-3 py-1.5 text-[10px] font-bold rounded-lg border cursor-pointer transition-all active:scale-95 ${
                            routeVehicle === v.key ? 'bg-[#a6e3a1] text-[#11111b] border-[#a6e3a1]' : 'bg-transparent text-[#a6adc8] border-[#45475a] hover:border-[#a6e3a1]'
                          }`}
                        >
                          <span>{v.icon}</span> {v.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Waypoints list */}
              {isRouteMode && (
                <div className="flex flex-col gap-2">
                  <h4 className="text-xs font-bold text-[#cdd6f4] uppercase tracking-wide">Pontos do Roteiro ({waypoints.length})</h4>
                  {waypoints.length > 0 ? (
                    <div className="flex flex-col gap-1 max-h-[160px] overflow-y-auto border border-[#313244]/50 rounded-lg p-2 bg-[#181825]/50">
                      {waypoints.map((wp, idx) => {
                        const letter = String.fromCharCode(65 + idx);
                        let dist = 0;
                        if (idx > 0) {
                          dist = haversineDistance(waypoints[idx - 1].lat, waypoints[idx - 1].lon, wp.lat, wp.lon);
                        }
                        return (
                          <div
                            key={idx}
                            draggable
                            onDragStart={() => setDragIdx(idx)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => { if (dragIdx !== null && dragIdx !== idx) { reorderWaypoints(dragIdx, idx); setDragIdx(null); } }}
                            className={`flex items-center gap-2 p-2 rounded-lg text-[10px] transition-colors cursor-grab active:cursor-grabbing ${
                              dragIdx === idx ? 'bg-[#89b4fa]/20 border border-[#89b4fa]' : 'hover:bg-[#313244]/30 border border-transparent'
                            }`}
                          >
                            <div className="w-5 h-5 rounded-full bg-[#89b4fa] text-[#11111b] text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                              {letter}
                            </div>
                            <span className="flex-1 text-[#a6adc8] truncate">
                              {wp.display_name.split(',').slice(0, 2).join(',')}
                            </span>
                            <span className="text-[9px] text-[#585b70] flex-shrink-0 w-12 text-right">
                              {idx === 0 ? '0m' : formatDistance(dist)}
                            </span>
                            <button
                              onClick={() => removeWaypoint(idx)}
                              className="w-5 h-5 flex items-center justify-center rounded bg-[#f38ba8] hover:bg-[#eba0ac] text-white cursor-pointer border-none active:scale-90 transition-all flex-shrink-0"
                            >
                              ✕
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-[10px] text-[#585b70] italic">Nenhum ponto adicionado no roteiro.</p>
                  )}
                </div>
              )}

              {/* Recentes list */}
              <div className="flex flex-col gap-2">
                {savedAddresses.length > 0 ? (
                  <div className="flex flex-col gap-1.5 max-h-[140px] overflow-y-auto border border-[#313244]/50 rounded-lg p-2 bg-[#181825]/50">
                    {savedAddresses.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center gap-2 p-1.5 rounded hover:bg-[#313244]/30 text-[10px] text-[#a6adc8]">
                        <button
                          onClick={() => selectRecentAddress(item)}
                          className="text-left flex-1 hover:text-white truncate cursor-pointer bg-transparent border-none"
                        >
                          📍 {item.display_name}
                        </button>
                        <button
                          onClick={() => {
                            setSavedAddresses(prev => {
                              const updated = prev.filter((_, i) => i !== idx);
                              localStorage.setItem(`emulator_saved_addresses_${device}`, JSON.stringify(updated));
                              return updated;
                            });
                          }}
                          className="text-red-400 hover:text-red-300 font-bold px-1.5 py-0.5 rounded bg-transparent hover:bg-red-400/10 cursor-pointer border-none"
                        >
                          Remover
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[10px] text-[#585b70] italic">Nenhum histórico disponível.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Logs de Erros de Iframe e Console */}
      {isErrorLogsModalOpen && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#1e1e2e] border border-[#313244] w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
            <div className="bg-[#181825] px-6 py-4 border-b border-[#313244] flex justify-between items-center">
              <h3 className="text-sm font-bold text-[#f38ba8] flex items-center gap-2">
                ⚠️ Console de Diagnóstico de Erros (Celular: {device})
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setErrorLogs([])}
                  className="text-white text-xs font-semibold px-3 py-1 rounded bg-[#e64553] hover:bg-[#d20f39] border-none cursor-pointer"
                >
                  Limpar Logs
                </button>
                <button
                  onClick={() => setIsErrorLogsModalOpen(false)}
                  className="text-[#a6adc8] hover:text-white text-xs font-semibold px-3 py-1 rounded bg-[#313244] border-none cursor-pointer"
                >
                  Fechar
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto flex-1 flex flex-col gap-3 font-mono text-[10px]">
              {errorLogs.length === 0 ? (
                <div className="text-[#585b70] italic text-center py-12">
                  Nenhum erro registrado até o momento. Tudo operando conforme esperado.
                </div>
              ) : (
                errorLogs.map((log, idx) => (
                  <div key={idx} className="bg-[#181825] border border-[#313244] rounded-lg p-3 flex flex-col gap-1.5 shadow">
                    <div className="flex justify-between items-center text-[8px] text-[#585b70] border-b border-[#313244]/50 pb-1">
                      <span className="font-bold text-[#f38ba8] uppercase">[{log.source}]</span>
                      <span>{log.time}</span>
                    </div>
                    <div className="text-[#cdd6f4] font-semibold break-all leading-normal">
                      {log.message}
                    </div>
                    {log.stack && (
                      <pre className="text-[#a6adc8] text-[8px] max-h-[100px] overflow-auto bg-[#11111b] p-2 rounded border border-[#313244]/60 whitespace-pre-wrap font-mono mt-1 leading-normal break-all">
                        {log.stack}
                      </pre>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- Modal Informativo do Modo de Visualização (EMU vs PWA) --- */}
      {isAboutModalOpen && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#1e1e2e] border border-[#313244] w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-[#181825] px-6 py-4 border-b border-[#313244] flex justify-between items-center">
              <h3 className="text-sm font-bold text-[#89b4fa] flex items-center gap-2">
                ℹ️ Entenda as Abas de Visualização
              </h3>
              <button
                onClick={() => setIsAboutModalOpen(false)}
                className="text-[#a6adc8] hover:text-white text-xs font-bold px-3 py-1 rounded bg-[#313244] border-none cursor-pointer"
              >
                Fechar
              </button>
            </div>

            {/* Body */}
            <div className="p-6 flex flex-col gap-4 text-left select-text">
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold text-[#89b4fa] uppercase tracking-wider">📱 Modo Emulador (EMU)</span>
                <p className="text-[11px] text-[#cdd6f4] leading-relaxed m-0">
                  Conecta a um celular Android físico real rodando no servidor via protocolo Scrcpy. Permite testar o app em um ambiente Android autêntico com GPS simulado, teclado físico e conexões de rede móvel/Wi-Fi integradas.
                </p>
              </div>

              <div className="flex flex-col gap-1 border-t border-[#313244] pt-3">
                <span className="text-[10px] font-bold text-[#b4befe] uppercase tracking-wider">🌐 Modo Web App (PWA)</span>
                <p className="text-[11px] text-[#cdd6f4] leading-relaxed m-0">
                  Roda o aplicativo diretamente no navegador como um Progressive Web App. Mais rápido para renderização visual local e depuração de estilos, mas sem as integrações nativas de hardware do celular.
                </p>
              </div>

              <div className="mt-2 bg-[#11111b]/80 border border-[#313244] rounded-lg p-2.5">
                <p className="text-[9px] text-[#a6adc8] italic leading-normal m-0 text-center">
                  Aviso: Este recurso de abas é uma ferramenta de teste integrada aos perfis de desenvolvimento do Aluno Conectado.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
